var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useLayoutEffect, useRef, useState, useCallback, } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/tableManipulativeComp.scss';
import MathEditor from './MathEditor';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import ButtonComponent from './ButtonComponent';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import customEventHandler, { setSkipTableDuplicate } from '../helper/customEvents';
import { ColorEnum } from './ColorPicker';
import { announce } from '../helper/announcer';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
import { computeKeypadPlacement, KEYPAD_MARGIN, KEYPAD_STACK_GAP, } from '../helper/tableKeypadPlacement';
// True when the device has ANY coarse pointer (touch screen), covering both
// touch-only iPads and iPads paired with a Magic Keyboard (whose primary
// pointer is fine/trackpad but whose screen is still coarse/touch).
// Using 'any-pointer: coarse' avoids the navigator.maxTouchPoints false-
// positive on Chrome/Mac, where the browser reports maxTouchPoints = 5 even
// though the machine has no touchscreen.
var HAS_TOUCH = typeof window !== 'undefined' && window.matchMedia('(any-pointer: coarse)').matches;
// Matches --keypad-width in mathEditor.scss. Used as the fallback when the
// portaled keypad hasn't been measured yet.
var KEYPAD_WIDTH = 523;
var cellIdCounter = 0;
var generateCellId = function () { return "cell_".concat(Date.now(), "_").concat(++cellIdCounter); };
var TableManipulativeComp = function (props) {
    var _a, _b, _c;
    var tabledata = props.tabledata, index = props.index;
    var tableSelectionId = "tableText_".concat(index);
    var _d = useContext(DataContext), table = _d.table, setTable = _d.setTable, selectedTool = _d.selectedTool, setSelectedTool = _d.setSelectedTool, panningEnable = _d.panningEnable, tool = _d.tool, color = _d.color, setContextMenu = _d.setContextMenu;
    var t = useTranslation().t;
    var _e = useMultiSelectDragSource(tableSelectionId, selectedTool), startMultiDrag = _e.startMultiDrag, moveMultiDrag = _e.moveMultiDrag, endMultiDrag = _e.endMultiDrag;
    // tabledata.data can be transiently undefined for one render (e.g. right after an
    // undo/redo swaps the table array). Fall back to safe defaults here so every hook
    // below always runs — never early-return before hooks are declared (Rules of Hooks).
    var _f = (_a = tabledata === null || tabledata === void 0 ? void 0 : tabledata.data) !== null && _a !== void 0 ? _a : {
        cellData: [],
        position: { x: 0, y: 0 },
        rowsHeight: [],
        colWidth: [],
        borderWidth: 2,
        lock: undefined,
    }, cellData = _f.cellData, position = _f.position, rowsHeight = _f.rowsHeight, colWidth = _f.colWidth, borderWidth = _f.borderWidth, lock = _f.lock;
    // ---------------- REFS ----------------
    var tableRef = useRef(null);
    var containerRef = useRef(null);
    var tableWrapperRef = useRef(null);
    var dragStartRef = useRef(null);
    var tablePosRef = useRef(position);
    var hasMoved = useRef(false);
    var dragStated = useRef(false);
    // Transient CSS-only bring-to-front boost while this table is actively
    // being dragged (dragStated is a ref and won't itself trigger the
    // re-render needed to reflect a zIndex change).
    var _g = useState(false), isDragging = _g[0], setIsDragging = _g[1];
    // Set to true when onMouseDown processes the event (past all guards). Used by
    // handleContainerClick to detect whether a preceding mousedown already handled
    // cell detection — if so, skip context menu (not a VO/keyboard activation).
    var onMouseDownHandledRef = useRef(false);
    // ---------------- GLOBAL STORE ----------------
    var _h = globalStore.useStoreState(function (state) { return state.playerStore; }), zoomLevel = _h.zoomLevel, canvasWidth = _h.canvasWidth, canvasHeight = _h.canvasHeight;
    var _j = globalStore.useStoreState(function (state) { return state.jsonDataStore; }), btnConfig = _j.btnConfig, bottomToolConfig = _j.bottomToolConfig;
    // ---------------- LOCK STATE ----------------
    var isPositionLocked = (lock === null || lock === void 0 ? void 0 : lock.positioning) === true;
    // ---------------- LOCAL STATE ----------------
    var _k = useState([]), colPos = _k[0], setColPos = _k[1];
    var _l = useState([]), rowPos = _l[0], setRowPos = _l[1];
    // Derive isFocused inline (same render cycle as selectedTool change) instead of
    // via useState+useEffect. The useState approach caused TWO render cycles:
    // render-1 (selectedTool updates, isFocused still false) then render-2
    // (isFocused=true, TableController+resize handles mount). Firefox lost the caret
    // during render-2's sibling DOM insertions. Computing it inline means ONE render
    // cycle — exactly like MathEditor does — so all DOM mutations happen together and
    // the browser's natural focus caret survives, just as it does in Dice Manipulative.
    var isFocused = selectedTool.some(function (item) {
        return typeof item === 'string' &&
            (item === "tableText_".concat(index) || item.startsWith("tableText_".concat(index, "_")));
    });
    var _m = useState(position), localTablePos = _m[0], setLocalTablePos = _m[1];
    var mathEditorRefs = useRef({});
    var _o = useState(false), isCellSelectMode = _o[0], setIsCellSelectMode = _o[1];
    var _p = useState(new Set()), selectedCells = _p[0], setSelectedCells = _p[1];
    // ---------------- PICKER STATE (OnCanvasPickerPattern) ----------------
    var _q = useState(false), pickerActive = _q[0], setPickerActive = _q[1];
    var pickerFirstBtnRef = useRef(null);
    // Tracks which cell was last activated in picker (for row/col operations from context menu)
    var pickerCellRef = useRef(null);
    // Tracks the cell key for the caret-restoration safety net in useEffect([isFocused]).
    // Set by onMouseDown when !isFocused; cleared after the useEffect handles it.
    var pendingFocusRef = useRef(null);
    var sentinelBtnRef = useRef(null);
    // Set when a cell is activated for toolbar focus (focusEditor:false). Suppresses the
    // onFocus Shift+Tab redirect for one event, since setSelectedTool hasn't committed yet.
    // Initialised true so the first programmatic focus() from GameController (table creation)
    // is never mis-classified as a Shift+Tab and redirected to the last picker button.
    var suppressFocusRedirectRef = useRef(true);
    // True when the pending focus was triggered by a touch event (iPad).
    // Prevents programmatic focus() in the useEffect, which would open the iOS keyboard.
    var touchFocusRef = useRef(false);
    // iPadOS software keyboard bug: the first Tab keydown after a tap-entered cell fires
    // with shiftKey=true (Shift+Tab) instead of shiftKey=false. Set true on tap entry,
    // cleared after the first Tab so that physical-keyboard Shift+Tab still works.
    var ignoreNextTabShiftRef = useRef(false);
    // Mirrors tabledata without being a closure dep — used in the capture-phase
    // math-field mousedown listener so it always reads the latest cell lock state.
    var tabledataRef = useRef(tabledata);
    tabledataRef.current = tabledata;
    var textFocusSnapshotRef = useRef(null);
    // Resize handles state
    var resizeHandleRef = useRef(null);
    var resizeStartRef = useRef(null);
    var resizeOldSnapshotRef = useRef(null);
    var saveStateGuardRef = useRef(false);
    // ---------------- TOOLBAR POSITIONING LOGIC ----------------
    // Ref to avoid re-creating callback on every localTablePos change during drag
    var localTablePosRef = useRef(localTablePos);
    localTablePosRef.current = localTablePos;
    var lastDispatchedToolbarPos = useRef(null);
    var toolbarAboveCellRef = useRef(false);
    // Keypad placement chosen by computeKeypadPlacement for the current frame.
    // updateToolbarPosition fills it (it already has every rect the geometry
    // needs); the rAF loop below applies it to the portaled keypad. Null when the
    // keypad is closed or the focused cell can't be resolved — the loop then falls
    // back to plain toolbar-anchored stacking.
    var keypadPlacementRef = useRef(null);
    // Last non-zero keypad size. offsetWidth/offsetHeight read 0 while the keypad
    // is display:none (the toolbar-collapsible mutual exclusion below), and the
    // real width is breakpoint-dependent (523 / 440 / 380 / 100vw), so falling
    // back to the desktop constant there would pick the wrong side.
    var keypadSizeRef = useRef({
        width: KEYPAD_WIDTH,
        height: 240,
    });
    var selectedToolRef = useRef(selectedTool);
    selectedToolRef.current = selectedTool;
    // The keypad this table owns: MathEditor portals it to body, so scope the
    // lookup by the cell editor's data-manipulative-id (`tableText_{index}_{r}_{c}`)
    // — otherwise a popup's keypad could be dragged around by this table's loop.
    var ownKeypadEl = useCallback(function () {
        return document.querySelector("body > .mathEditorParent[data-manipulative-id^=\"tableText_".concat(index, "_\"] .keypadWrapper"));
    }, [index]);
    var updateToolbarPosition = useCallback(function () {
        var _a, _b;
        if (!isFocused || !tableRef.current)
            return;
        var pos = localTablePosRef.current;
        var gap = 15;
        // Position toolbar below the focused cell using actual DOM positions
        var activeCell = selectedToolRef.current.find(function (id) { return id.startsWith("tableText_".concat(index, "_")); });
        var cellTop = 0;
        var cellBottom = tableRef.current.offsetHeight;
        var cellX = pos.x;
        // Focused cell in viewport coords — the keypad geometry works in screen space
        // (zoom/pan already applied), so take it straight off the DOM.
        var focusedCellRect = null;
        if (activeCell && tableRef.current) {
            var parts = activeCell.split('_');
            var rowIdx = parseInt(parts[2]);
            var colIdx = parseInt(parts[3]);
            var row = (_a = tableRef.current.querySelector("tbody")) === null || _a === void 0 ? void 0 : _a.children[rowIdx];
            var cell = row === null || row === void 0 ? void 0 : row.children[colIdx];
            if (row && cell) {
                cellTop = row.offsetTop;
                cellBottom = row.offsetTop + row.offsetHeight;
                cellX = pos.x + cell.offsetLeft;
                focusedCellRect = cell.getBoundingClientRect();
            }
        }
        // Position and clamp the toolbar so it stays fully within the viewport.
        // The toolbar is position:absolute inside .mainHolder (canvas transform), so
        // getBoundingClientRect() values already include the zoom scale factor.
        // Screen coords: screenX = mhRect.left + canvasX * zoom
        var toolbarEl = document.querySelector('.bottomPanelTool');
        var mainHolderEl = document.querySelector('.mainHolder');
        var sidebarEl = document.querySelector('.sidebar');
        var y2 = pos.y + cellBottom + gap;
        if (toolbarEl && mainHolderEl) {
            var toolbarRect = toolbarEl.getBoundingClientRect();
            var t_1 = getComputedStyle(mainHolderEl).transform;
            var m = t_1 === null || t_1 === void 0 ? void 0 : t_1.match(/^matrix\(([^,]+)/);
            var zv = m ? parseFloat(m[1]) || 1 : 1;
            var mhRect = mainHolderEl.getBoundingClientRect();
            var sidebarRight = sidebarEl ? sidebarEl.getBoundingClientRect().right : 0;
            // X: keep toolbar left >= sidebar right, right <= viewport right
            var minCellX = (sidebarRight + 10 - mhRect.left) / zv;
            var maxCellX = (window.innerWidth - 10 - mhRect.left - toolbarRect.width) / zv;
            cellX = Math.max(minCellX, Math.min(cellX, maxCellX));
            // Y positioning: default is below the focused cell. Flip above only when needed.
            var ownKeypad = ownKeypadEl();
            var keypadOpen = !!ownKeypad;
            var cellTopScreen = mhRect.top + (pos.y + cellTop) * zv;
            var cellBottomScreen = mhRect.top + (pos.y + cellBottom) * zv;
            var headerEl = document.querySelector('.appHeader');
            var headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
            var wasToolbarAbove = toolbarAboveCellRef.current;
            var prevKeypadSide = (_b = keypadPlacementRef.current) === null || _b === void 0 ? void 0 : _b.side;
            toolbarAboveCellRef.current = false; // default: toolbar below cell
            keypadPlacementRef.current = null;
            if (keypadOpen && focusedCellRect) {
                // Let the placement helper pick the side. It refuses positions that would
                // cover the table (every covered cell is unclickable while the keypad is
                // up) and falls back to sitting beside the table when neither the space
                // below nor above the focused row can hold the toolbar + keypad stack.
                if (ownKeypad && ownKeypad.offsetWidth && ownKeypad.offsetHeight) {
                    keypadSizeRef.current = {
                        width: ownKeypad.offsetWidth,
                        height: ownKeypad.offsetHeight,
                    };
                }
                var _c = keypadSizeRef.current, keypadW = _c.width, keypadH = _c.height;
                var tableRect = tableRef.current.getBoundingClientRect();
                // Whichever side the toolbar is on right now is known exactly from its
                // live rect; the opposite side is derived from the focused cell. The
                // `+ 10` mirrors the canvas-to-screen offset baked into .bottomPanelTool.
                var placement = computeKeypadPlacement({
                    cell: {
                        top: focusedCellRect.top,
                        left: focusedCellRect.left,
                        bottom: focusedCellRect.bottom,
                        right: focusedCellRect.right,
                    },
                    table: {
                        top: tableRect.top,
                        left: tableRect.left,
                        bottom: tableRect.bottom,
                        right: tableRect.right,
                    },
                    toolbarHeight: toolbarRect.height,
                    toolbarWidth: toolbarRect.width,
                    toolbarLeft: toolbarRect.left,
                    toolbarTopIfBelow: wasToolbarAbove
                        ? cellBottomScreen + (gap + 10) * zv
                        : toolbarRect.top,
                    toolbarTopIfAbove: wasToolbarAbove
                        ? toolbarRect.top
                        : cellTopScreen - toolbarRect.height - 10,
                    keypadWidth: keypadW,
                    keypadHeight: keypadH,
                    viewport: { width: window.innerWidth, height: window.innerHeight },
                    headerBottom: headerBottom,
                    sidebarRight: sidebarRight,
                    // Sticky side: with the loop re-scoring every frame, an equally-good
                    // alternative must not win, or the keypad teleports mid-drag/zoom.
                    previousSide: prevKeypadSide,
                });
                keypadPlacementRef.current = placement;
                toolbarAboveCellRef.current = placement.toolbarAboveCell;
                if (placement.toolbarAboveCell) {
                    y2 = (cellTopScreen - toolbarRect.height - 10 - mhRect.top) / zv - 10;
                }
            }
            if (!keypadOpen || !focusedCellRect) {
                // Default: below cell. Flip above focused cell if toolbar would overflow the bottom.
                var toolbarBottomScreen = mhRect.top + (y2 + 10) * zv + toolbarRect.height;
                if (toolbarBottomScreen > window.innerHeight - 10) {
                    var targetTopScreen = cellTopScreen - toolbarRect.height - 10;
                    y2 = (targetTopScreen - mhRect.top) / zv - 10;
                }
            }
        }
        var newPos = { x1: cellX, y2: y2 };
        // Skip dispatch if position hasn't changed (avoid unnecessary React re-renders)
        var last = lastDispatchedToolbarPos.current;
        if (last && last.x1 === newPos.x1 && last.y2 === newPos.y2)
            return;
        lastDispatchedToolbarPos.current = newPos;
        customEventHandler.dispatchEvent('tableToolbarPosition', __assign(__assign({}, newPos), { aboveCell: toolbarAboveCellRef.current }));
    }, [isFocused, index, ownKeypadEl]);
    useEffect(function () {
        if (!isFocused) {
            lastDispatchedToolbarPos.current = null;
            customEventHandler.dispatchEvent('tableToolbarPosition', null);
            return;
        }
        // Double-rAF: let the browser do a full layout pass after mount so
        // tableRef.current.offsetHeight is the real painted height before we
        // calculate the toolbar position for the first time.
        requestAnimationFrame(function () { return requestAnimationFrame(function () { return updateToolbarPosition(); }); });
        // ResizeObserver tracks shrinking/expanding instantly
        var observer = new ResizeObserver(function () {
            updateToolbarPosition();
        });
        if (tableRef.current) {
            observer.observe(tableRef.current);
        }
        // Animation loop tracks dragging/panning + keypad positioning
        var rafId;
        var loop = function () {
            updateToolbarPosition();
            // Position MathEditor keypad. With isTextUpdate=false, MathEditor portals the keypad
            // to document.body. We override with position:fixed (prevents body scroll extension)
            // and keep it within the viewport with X/Y clamping + above-flip.
            var toolbar = document.querySelector('.bottomPanelTool');
            if (toolbar) {
                var portaledKeypad = ownKeypadEl();
                if (portaledKeypad) {
                    var placement = keypadPlacementRef.current;
                    var top_1;
                    var left = void 0;
                    if (placement) {
                        // Chosen by computeKeypadPlacement in updateToolbarPosition (called at
                        // the top of this same frame) — below / above the focused row, or
                        // beside the table when a stacked position would cover it.
                        top_1 = placement.top;
                        left = placement.left;
                    }
                    else {
                        // No focused cell to reason about (e.g. table-level selection): keep
                        // the plain toolbar-anchored stacking, clamped to the viewport.
                        var toolbarRect = toolbar.getBoundingClientRect();
                        var pkpH = portaledKeypad.offsetHeight || 240;
                        var sidebar = document.querySelector('.sidebar');
                        var minLeft = sidebar
                            ? sidebar.getBoundingClientRect().right + KEYPAD_MARGIN
                            : KEYPAD_MARGIN;
                        var headerEl = document.querySelector('.appHeader');
                        var headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
                        left = Math.max(minLeft, Math.min(toolbarRect.left, window.innerWidth - KEYPAD_WIDTH - KEYPAD_MARGIN));
                        top_1 = toolbarAboveCellRef.current
                            ? Math.max(headerBottom + KEYPAD_MARGIN, toolbarRect.top - pkpH - KEYPAD_STACK_GAP)
                            : Math.min(toolbarRect.bottom + KEYPAD_STACK_GAP, window.innerHeight - pkpH - KEYPAD_MARGIN);
                    }
                    portaledKeypad.style.position = 'fixed';
                    portaledKeypad.style.top = "".concat(top_1, "px");
                    portaledKeypad.style.left = "".concat(left, "px");
                }
                // Hide keypad when a toolbar collapsible (e.g. thickness) is open
                var collapsibleOpen = toolbar.querySelector('.expanded_bottom, .expanding_bottom');
                var portalKp = portaledKeypad;
                if (collapsibleOpen) {
                    if (portalKp)
                        portalKp.style.display = 'none';
                }
                else {
                    if (portalKp && portalKp.style.display === 'none')
                        portalKp.style.display = '';
                }
            }
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
        return function () {
            observer.disconnect();
            cancelAnimationFrame(rafId);
            customEventHandler.dispatchEvent('tableToolbarPosition', null);
        };
    }, [isFocused, updateToolbarPosition]);
    // Reposition toolbar when active cell changes
    useEffect(function () {
        if (isFocused)
            updateToolbarPosition();
    }, [selectedTool]);
    // ---------------- SYNC POSITION ----------------
    useEffect(function () {
        if (!dragStated.current) {
            setLocalTablePos(position);
        }
        tablePosRef.current = position;
    }, [position.x, position.y]);
    /** ---------------- IS FOCUSED SIDE-EFFECTS ---------------- */
    // isFocused is now computed inline above; this effect only handles cleanup
    // when focus is lost (resetting cell-select mode and selection set).
    useEffect(function () {
        if (!isFocused) {
            setIsCellSelectMode(false);
            setSelectedCells(new Set());
            setPickerActive(false);
            // Clear cellSelectMode flag on table data
            setTable(function (prev) {
                var _a, _b;
                if (!((_b = (_a = prev[index]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.cellSelectMode))
                    return prev;
                var nt = __spreadArray([], prev, true);
                nt[index] = __assign(__assign({}, nt[index]), { data: __assign(__assign({}, nt[index].data), { cellSelectMode: false }) });
                return nt;
            });
        }
    }, [isFocused]);
    // Capture snapshot on cell focus, save undo on blur if text changed
    var prevCellRef = useRef(null);
    useEffect(function () {
        var _a, _b;
        var activeCellId = selectedTool.find(function (id) { return id.startsWith("tableText_".concat(index, "_")); });
        var cellKey = activeCellId && activeCellId.split('_').length >= 4 ? activeCellId : null;
        // Cell changed or lost focus — save if text was edited
        if (prevCellRef.current !== cellKey && textFocusSnapshotRef.current) {
            // Flush the previous cell's MathEditor content synchronously before
            // taking the snapshot. MathEditor debounces its onChange cb by 500ms,
            // so if the user clicks from one cell to another quickly the table
            // state may not yet contain the previous cell's text — causing two
            // edits to merge into a single undo entry.
            var snapshotTable_1 = tabledataRef.current;
            var prevRefKey = (_a = prevCellRef.current) === null || _a === void 0 ? void 0 : _a.replace('tableText_', '');
            var prevEditor = prevRefKey ? mathEditorRefs.current[prevRefKey] : null;
            if (prevEditor) {
                var parts = prevRefKey.split('_').map(Number);
                var rowIdx = parts[1], colIdx = parts[2];
                var cellIdx = rowIdx * numCols + colIdx;
                var content = prevEditor.getContent();
                var currentCell = snapshotTable_1.data.cellData[cellIdx];
                if (currentCell && JSON.stringify((_b = currentCell.data) === null || _b === void 0 ? void 0 : _b.text) !== JSON.stringify(content)) {
                    // Build a patched snapshot that includes the pending text and
                    // also push it into table state so React stays in sync.
                    var newCellData = __spreadArray([], snapshotTable_1.data.cellData, true);
                    newCellData[cellIdx] = __assign(__assign({}, newCellData[cellIdx]), { data: __assign(__assign({}, newCellData[cellIdx].data), { text: content }) });
                    snapshotTable_1 = __assign(__assign({}, snapshotTable_1), { data: __assign(__assign({}, snapshotTable_1.data), { cellData: newCellData }) });
                    tabledataRef.current = snapshotTable_1;
                    setTable(function (prev) {
                        var newTable = __spreadArray([], prev, true);
                        newTable[index] = snapshotTable_1;
                        return newTable;
                    });
                }
            }
            var currentSnapshot = JSON.stringify(snapshotTable_1);
            if (textFocusSnapshotRef.current !== currentSnapshot) {
                saveState({
                    type: 'table',
                    id: index,
                    propertyName: 'textEdit',
                    old: textFocusSnapshotRef.current,
                    new: currentSnapshot,
                });
            }
            textFocusSnapshotRef.current = null;
        }
        // New cell focused — capture snapshot
        if (cellKey && prevCellRef.current !== cellKey) {
            textFocusSnapshotRef.current = JSON.stringify(tabledataRef.current);
        }
        prevCellRef.current = cellKey;
    }, [selectedTool, index]);
    // Sync formatting active states for toolbar buttons when in table-level or cell-select mode
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f;
        var isTableLevel = selectedTool.some(function (t) { return t === "tableText_".concat(index); });
        var activeCellId = !isCellSelectMode
            ? selectedTool.find(function (id) { return id.startsWith("tableText_".concat(index, "_")) && id.split('_').length >= 4; })
            : undefined;
        var applyToAll = isCellSelectMode ? selectedCells.size === 0 : isTableLevel;
        if (!applyToAll && !(isCellSelectMode && selectedCells.size > 0) && !activeCellId)
            return;
        // Determine which cells to check
        var cells = [];
        if (applyToAll) {
            cells.push.apply(cells, cellData);
        }
        else if (activeCellId) {
            var parts = activeCellId.split('_');
            var numColsHere = (_d = (_c = (_b = (_a = tabledataRef.current) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.colWidth) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 1;
            var ci = Number(parts[2]) * numColsHere + Number(parts[3]);
            if (cellData[ci])
                cells.push(cellData[ci]);
        }
        else {
            selectedCells.forEach(function (ck) {
                var _a = ck.split('_').map(Number), r = _a[0], c = _a[1];
                var ci = r * numCols + c;
                if (cellData[ci])
                    cells.push(cellData[ci]);
            });
        }
        if (cells.length === 0)
            return;
        // Active = ALL selected cells have the flag set
        var allBold = cells.every(function (c) { var _a; return (_a = c === null || c === void 0 ? void 0 : c.data) === null || _a === void 0 ? void 0 : _a.bold; });
        var allItalic = cells.every(function (c) { var _a; return (_a = c === null || c === void 0 ? void 0 : c.data) === null || _a === void 0 ? void 0 : _a.italic; });
        var allUnderline = cells.every(function (c) { var _a; return (_a = c === null || c === void 0 ? void 0 : c.data) === null || _a === void 0 ? void 0 : _a.underline; });
        var alignment = cells.every(function (c) { var _a, _b, _c; return ((_a = c === null || c === void 0 ? void 0 : c.data) === null || _a === void 0 ? void 0 : _a.alignment) === ((_c = (_b = cells[0]) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.alignment); })
            ? ((_f = (_e = cells[0]) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.alignment) || 'left'
            : '';
        setTimeout(function () {
            customEventHandler.dispatchEvent('formattingStateChanged', {
                bold: allBold,
                italic: allItalic,
                underline: allUnderline,
                leftAlign: alignment === 'left',
                centerAlign: alignment === 'center',
                rightAlign: alignment === 'right',
            });
        }, 0);
    }, [selectedTool, isCellSelectMode, selectedCells, cellData, index]);
    /** ---------------- LOCKED CELL MATH-FIELD CLICK FIX (capture phase) ---------------- */
    // math-field custom elements register a native mousedown handler (in MathEditor) that
    // calls e.stopPropagation(). This prevents the event from bubbling to our React
    // onMouseDown on the table container, so selectedTool never gets updated when clicking
    // a locked cell that contains a math-field.
    //
    // Fix: register a capture-phase listener on the container. Capture fires BEFORE the
    // math-field's stopPropagation, so we can update selectedTool here for locked cells.
    // Non-locked cells with math-field are unaffected (MathEditor's focusHandler handles them).
    useEffect(function () {
        var el = containerRef.current;
        if (!el)
            return;
        var handleMathFieldMousedown = function (e) {
            var _a, _b, _c;
            var target = e.target;
            // Only intercept clicks that land directly on a math-field element.
            if (target.tagName.toLowerCase() !== 'math-field')
                return;
            var td = target.closest('td');
            if (!td || !tableRef.current)
                return;
            var tbody = tableRef.current.querySelector('tbody');
            var row = td.closest('tr');
            if (!tbody || !row)
                return;
            var rowIndex = Array.from(tbody.children).indexOf(row);
            var colIndex = Array.from(row.children).indexOf(td);
            if (rowIndex < 0 || colIndex < 0)
                return;
            var cols = tabledataRef.current.data.colWidth.length;
            var isLockedCell = ((_c = (_b = (_a = tabledataRef.current.data.cellData[rowIndex * cols + colIndex]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing) ===
                true;
            if (!isLockedCell) {
                // Unlocked cell: still dispatch the lock state so the toolbar indicator reflects
                // this cell's state. MathEditor's focusHandler will update selectedTool, but
                // stopPropagation prevents React onMouseDown from firing, so we dispatch here.
                customEventHandler.dispatchEvent('tableCellLockState', { locked: false });
                return;
            }
            // Locked cell: math-field's stopPropagation will prevent React onMouseDown from firing.
            // Update selectedTool here so the sidebar lock icon reflects this cell's state.
            if (!isFocused) {
                // Match the !isFocused branch in onMouseDown: prevent default focus behaviour.
                e.preventDefault();
                pendingFocusRef.current = null; // No caret to place for a locked cell.
            }
            // Notify toolbar that the selected cell is locked (fires synchronously, same batch).
            customEventHandler.dispatchEvent('tableCellLockState', { locked: true });
            setSelectedTool(["tableText_".concat(index, "_").concat(rowIndex, "_").concat(colIndex)]);
        };
        el.addEventListener('mousedown', handleMathFieldMousedown, true);
        el.addEventListener('touchstart', handleMathFieldMousedown, true);
        return function () {
            el.removeEventListener('mousedown', handleMathFieldMousedown, true);
            el.removeEventListener('touchstart', handleMathFieldMousedown, true);
        };
    }, [index, setSelectedTool]); // index is stable; setSelectedTool is stable from context.
    // iOS 26 fix: focusing/tapping a math-field inside a table cell makes Safari
    // scroll the whiteboard's CustomScrollbar content (.customScrollbarContent) to
    // bring the field into view. The MathEditor focus-scroll lock only pins
    // `window`, which stays at 0 here, so that inner scroll is never restored —
    // the canvas "jumps" and never returns. Pin the real scroll container while a
    // math-field inside THIS table is focused. Scoped to this table: it only acts
    // when document.activeElement is a math-field within containerRef, so other
    // manipulatives and normal canvas scrolling are unaffected.
    useEffect(function () {
        var savedRef = { top: 0, left: 0 };
        var armed = false;
        var restoring = false;
        var getScroller = function () {
            var _a, _b;
            return (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent')) !== null && _b !== void 0 ? _b : document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        };
        var tableMathFocused = function () {
            var _a;
            var a = document.activeElement;
            return !!a && a.tagName === 'MATH-FIELD' && !!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(a));
        };
        var onFocusIn = function (e) {
            var _a;
            var t = e.target;
            if ((t === null || t === void 0 ? void 0 : t.tagName) === 'MATH-FIELD' && ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(t))) {
                var sc = getScroller();
                if (sc) {
                    savedRef.top = sc.scrollTop;
                    savedRef.left = sc.scrollLeft;
                    armed = true;
                }
            }
        };
        var onFocusOut = function () {
            // Disarm once focus genuinely leaves the table's math editing context.
            setTimeout(function () {
                if (!tableMathFocused())
                    armed = false;
            }, 0);
        };
        var onScroll = function (e) {
            var _a;
            if (!armed || restoring)
                return;
            var sc = e.target;
            if (!sc || !((_a = sc.classList) === null || _a === void 0 ? void 0 : _a.contains('customScrollbarContent')))
                return;
            if (!tableMathFocused())
                return;
            if (sc.scrollTop === savedRef.top && sc.scrollLeft === savedRef.left)
                return;
            restoring = true;
            sc.scrollTop = savedRef.top;
            sc.scrollLeft = savedRef.left;
            requestAnimationFrame(function () {
                restoring = false;
            });
        };
        document.addEventListener('focusin', onFocusIn, true);
        document.addEventListener('focusout', onFocusOut, true);
        document.addEventListener('scroll', onScroll, true);
        return function () {
            document.removeEventListener('focusin', onFocusIn, true);
            document.removeEventListener('focusout', onFocusOut, true);
            document.removeEventListener('scroll', onScroll, true);
        };
    }, []);
    /** ---------------- FIRST-CLICK CARET SAFETY NET (Win/Firefox) ---------------- */
    // The real fix for Firefox is in the JSX below: TableController and resize handles
    // are now always mounted (never inserted/removed), so Firefox never loses the caret
    // due to sibling DOM insertions when isFocused becomes true.
    //
    // This effect is kept as a safety net: if the caret is ever lost despite the above
    // (e.g. MathEditor's own effects run and blur the element), we restore it here.
    // It runs after paint (useEffect), which is intentional — by then all child effects
    // (including MathEditor's) have already run, so this is the final word.
    useEffect(function () {
        if (!pendingFocusRef.current || !isFocused)
            return;
        var focusCellKey = pendingFocusRef.current;
        pendingFocusRef.current = null;
        if (!tableRef.current)
            return;
        var parts = focusCellKey.split('_');
        var rIdx = parseInt(parts[1]);
        var cIdx = parseInt(parts[2]);
        var tbody = tableRef.current.querySelector('tbody');
        var row = tbody ? Array.from(tbody.children)[rIdx] : null;
        var cellTd = row ? Array.from(row.children)[cIdx] : null;
        var editorEl = cellTd === null || cellTd === void 0 ? void 0 : cellTd.querySelector('.editorDiv');
        if (!editorEl)
            return;
        // On touch (iPad): skip programmatic focus — iOS opens the keyboard for any
        // focus() call within the user-activation window, even from a useEffect.
        // The table is already "focused" in React state (isFocused=true); the user
        // can tap the cell again to open the keyboard when they want to type.
        if (touchFocusRef.current) {
            touchFocusRef.current = false;
            return;
        }
        // e.preventDefault() in onMouseDown prevented the browser from naturally
        // focusing the element, so we MUST do it here. This runs after paint and
        // after all child useEffects (including MathEditor's), so every DOM mutation
        // from the React render is already committed — Firefox cannot lose the caret.
        editorEl.focus({ preventScroll: true });
        var sel = window.getSelection();
        if (sel) {
            var range = document.createRange();
            range.selectNodeContents(editorEl);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }
        // selectedTool (not just isFocused) so the effect re-fires when VoiceOver double-taps
        // a picker button while the table is already focused — isFocused stays true in that
        // case and would never re-trigger the effect.
    }, [selectedTool]);
    useEffect(function () {
        customEventHandler.dispatchEvent('selectModeChanged', { active: isCellSelectMode });
    }, [isCellSelectMode]);
    useEffect(function () {
        if (!isCellSelectMode || selectedCells.size === 0)
            return;
        var allLocked = Array.from(selectedCells).every(function (ck) {
            var _a, _b, _c;
            var _d = ck.split('_').map(Number), r = _d[0], c = _d[1];
            return ((_c = (_b = (_a = tabledataRef.current.data.cellData[r * numCols + c]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing) === true;
        });
        customEventHandler.dispatchEvent('tableCellLockState', { locked: allLocked });
    }, [selectedCells]);
    /** ---------------- CLICK OUTSIDE / SCROLL PREVENTION ---------------- */
    useEffect(function () {
        if (!isFocused)
            return;
        var handleClickOutside = function (e) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
            if (!containerRef.current)
                return;
            var target = e.target;
            if (containerRef.current.contains(target))
                return;
            if ((_b = (_a = target).closest) === null || _b === void 0 ? void 0 : _b.call(_a, '.toolBottomPanelContainer'))
                return;
            if ((_d = (_c = target).closest) === null || _d === void 0 ? void 0 : _d.call(_c, '.bottomPanelToolContainer'))
                return;
            if ((_f = (_e = target).closest) === null || _f === void 0 ? void 0 : _f.call(_e, '.bottomPanelTool'))
                return;
            if ((_h = (_g = target).closest) === null || _h === void 0 ? void 0 : _h.call(_g, '.drawer-container'))
                return;
            if ((_k = (_j = target).closest) === null || _k === void 0 ? void 0 : _k.call(_j, '.rightSideToolPanel'))
                return;
            if ((_m = (_l = target).closest) === null || _m === void 0 ? void 0 : _m.call(_l, '.sidebar'))
                return;
            if ((_p = (_o = target).closest) === null || _p === void 0 ? void 0 : _p.call(_o, '.keypadWrapper'))
                return;
            if ((_r = (_q = target).closest) === null || _r === void 0 ? void 0 : _r.call(_q, '.collapsibleOptionHolder'))
                return;
            if ((_t = (_s = target).closest) === null || _t === void 0 ? void 0 : _t.call(_s, '.collapsibleRight'))
                return;
            if ((_v = (_u = target).closest) === null || _v === void 0 ? void 0 : _v.call(_u, '.shape-context-menu-overlay'))
                return;
            // Dragging the multi-select resizeHolder overlay is not a "click outside" — it's a
            // group-move gesture that may include this table. Mirrors WhiteBoard.tsx's own
            // isOverResizeHolder guard, which this listener otherwise has no equivalent of (the
            // resizeHolder div has neither data-manipulative-id nor data-group-id, so the checks
            // below can't recognize it).
            if ((_x = (_w = target).closest) === null || _x === void 0 ? void 0 : _x.call(_w, '.resizeHolder'))
                return;
            // A Shift / Ctrl / Cmd + click is an explicit multi-select ADD/toggle gesture — the
            // clicked item is often NOT yet in selectedTool (that's the whole point: this click is
            // what's about to add it), so the "already part of selectedTool" check below can never
            // protect it. Without this guard, shift-clicking an unselected item while others were
            // already selected wiped the entire prior selection ~0ms before selectTool() could add
            // the new item. Mirrors the identical isMultiSelectGesture guard in WhiteBoard.tsx.
            var isMultiSelectGesture = e.shiftKey || e.ctrlKey || e.metaKey;
            if (isMultiSelectGesture)
                return;
            // If the mousedown lands on another item that's ALREADY part of the current
            // multi-select (e.g. the user is starting a multi-select drag using some other
            // selected manipulative as leader, with this table also selected), this is NOT a
            // "click outside" — it's a continuation of the existing selection gesture. Clearing
            // selectedTool here would wipe the whole multi-select ~0ms into the drag, since this
            // listener is on `document` with capture:true and fires for every mousedown anywhere,
            // including drag-start mousedowns on other manipulatives.
            var targetManipulativeEl = (_z = (_y = target).closest) === null || _z === void 0 ? void 0 : _z.call(_y, '[data-manipulative-id]');
            var targetManipulativeId = targetManipulativeEl === null || targetManipulativeEl === void 0 ? void 0 : targetManipulativeEl.getAttribute('data-manipulative-id');
            // When the clicked item is part of a GROUP, selectedTool holds the group's own id, not
            // each member's individual id (e.g. NumberAndMoneyPieceManipulative.tsx tags each piece
            // with both data-manipulative-id={its own id} and data-group-id={the group's id}).
            // Checking only the individual id here missed grouped items entirely, still wiping the
            // whole multi-select the instant a grouped item's drag started.
            var targetGroupId = targetManipulativeEl === null || targetManipulativeEl === void 0 ? void 0 : targetManipulativeEl.getAttribute('data-group-id');
            if (targetManipulativeId && selectedToolRef.current.includes(targetManipulativeId))
                return;
            if (targetGroupId && selectedToolRef.current.includes(targetGroupId))
                return;
            setTimeout(function () { return setSelectedTool([]); }, 0);
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('touchstart', handleClickOutside, true);
        return function () {
            document.removeEventListener('mousedown', handleClickOutside, true);
            document.removeEventListener('touchstart', handleClickOutside, true);
        };
    }, [isFocused, setSelectedTool]);
    useEffect(function () {
        if (!isFocused)
            return;
        var scrollContainer = document.querySelector('.whitepaper');
        if (!scrollContainer)
            return;
        var savedScrollLeft = scrollContainer.scrollLeft;
        var savedScrollTop = scrollContainer.scrollTop;
        var handleClick = function (e) {
            var _a;
            if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)))
                return;
            savedScrollLeft = scrollContainer.scrollLeft;
            savedScrollTop = scrollContainer.scrollTop;
            var checkTimes = [0, 10, 20, 50, 100, 150];
            checkTimes.forEach(function (delay) {
                setTimeout(function () {
                    if (scrollContainer.scrollLeft !== savedScrollLeft ||
                        scrollContainer.scrollTop !== savedScrollTop) {
                        scrollContainer.scrollLeft = savedScrollLeft;
                        scrollContainer.scrollTop = savedScrollTop;
                    }
                }, delay);
            });
        };
        document.addEventListener('mousedown', handleClick, true);
        document.addEventListener('touchstart', handleClick, true);
        return function () {
            document.removeEventListener('mousedown', handleClick, true);
            document.removeEventListener('touchstart', handleClick, true);
        };
    }, [isFocused]);
    /** ---------------- DATA HELPERS ---------------- */
    var tableRows = [];
    var numCols = colWidth.length;
    var numRows = rowsHeight.length;
    for (var i = 0; i < numRows; i++) {
        var start = i * numCols;
        var end = start + numCols;
        tableRows.push(cellData.slice(start, end));
    }
    var btnData = {
        add: btnConfig.plus,
        delete: btnConfig.minus,
    };
    /** ---------------- CRUD OPERATIONS ---------------- */
    var onUpdateText = function (content, type, idx) {
        var _a, _b, _c;
        var _d = String(idx).split('_').map(Number), tableIndex = _d[0], rowIndex = _d[1], colIndex = _d[2];
        var cellIdx = rowIndex * numCols + colIndex;
        if ((_c = (_b = (_a = cellData[cellIdx]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing)
            return;
        var oldSnapshot = type !== 'text' && type !== 'fontSize' ? JSON.stringify(tabledataRef.current) : null;
        saveStateGuardRef.current = false;
        setTable(function (prev) {
            var _a, _b;
            var newTable = __spreadArray([], prev, true);
            var currentTable = __assign({}, newTable[tableIndex]);
            var data = __assign({}, currentTable.data);
            var newCellData = __spreadArray([], data.cellData, true);
            var targetCell = __assign({}, newCellData[cellIdx]);
            switch (type) {
                case 'text':
                    targetCell.data = __assign(__assign({}, targetCell.data), { text: content });
                    break;
                case 'alignment':
                    targetCell.data = __assign(__assign({}, targetCell.data), { alignment: (_a = content) !== null && _a !== void 0 ? _a : 'left' });
                    break;
                case 'fontSize': {
                    var cellText = (_b = targetCell.data) === null || _b === void 0 ? void 0 : _b.text;
                    var isEmpty = !cellText ||
                        (typeof cellText === 'string' && !cellText.trim()) ||
                        (Array.isArray(cellText) &&
                            cellText.every(function (item) { var _a; return !((_a = item === null || item === void 0 ? void 0 : item.value) === null || _a === void 0 ? void 0 : _a.trim()) && (item === null || item === void 0 ? void 0 : item.type) !== 'math'; }));
                    if (isEmpty)
                        break;
                    var currentSize = targetCell.data.size || 25;
                    targetCell.data = __assign(__assign({}, targetCell.data), { size: content === 'down' ? Math.max(15, currentSize - 5) : Math.min(100, currentSize + 5) });
                    break;
                }
                case 'backgroundColor':
                    targetCell.data = __assign(__assign({}, targetCell.data), { backgroundColor: content });
                    break;
            }
            newCellData[cellIdx] = targetCell;
            data.cellData = newCellData;
            currentTable.data = data;
            newTable[tableIndex] = currentTable;
            if (oldSnapshot && !saveStateGuardRef.current) {
                var ns = JSON.stringify(currentTable);
                if (oldSnapshot !== ns) {
                    saveStateGuardRef.current = true;
                    saveState({
                        type: 'table',
                        id: tableIndex,
                        propertyName: type,
                        old: oldSnapshot,
                        new: ns,
                    });
                }
            }
            return newTable;
        });
    };
    /** Flush any pending text-edit snapshot so structural operations don't create spurious undo entries. */
    /** Flush any pending text-edit snapshot so structural operations don't create spurious undo entries.
     *  Also syncs any debounced MathEditor content that hasn't been written to table state yet. */
    var flushTextSnapshot = function () {
        var _a, _b;
        if (!textFocusSnapshotRef.current)
            return;
        // Sync pending MathEditor content for the active cell (same race-condition
        // guard as the cell-switch useEffect above).
        var snapshotTable = tabledataRef.current;
        var prevRefKey = (_a = prevCellRef.current) === null || _a === void 0 ? void 0 : _a.replace('tableText_', '');
        var prevEditor = prevRefKey ? mathEditorRefs.current[prevRefKey] : null;
        if (prevEditor) {
            var parts = prevRefKey.split('_').map(Number);
            var rowIdx = parts[1], colIdx = parts[2];
            var cellIdx = rowIdx * numCols + colIdx;
            var content = prevEditor.getContent();
            var currentCell = snapshotTable.data.cellData[cellIdx];
            if (currentCell && JSON.stringify((_b = currentCell.data) === null || _b === void 0 ? void 0 : _b.text) !== JSON.stringify(content)) {
                var newCellData = __spreadArray([], snapshotTable.data.cellData, true);
                newCellData[cellIdx] = __assign(__assign({}, newCellData[cellIdx]), { data: __assign(__assign({}, newCellData[cellIdx].data), { text: content }) });
                snapshotTable = __assign(__assign({}, snapshotTable), { data: __assign(__assign({}, snapshotTable.data), { cellData: newCellData }) });
                tabledataRef.current = snapshotTable;
                setTable(function (prev) {
                    var newTable = __spreadArray([], prev, true);
                    newTable[index] = snapshotTable;
                    return newTable;
                });
            }
        }
        var currentSnapshot = JSON.stringify(snapshotTable);
        if (textFocusSnapshotRef.current !== currentSnapshot) {
            saveState({
                type: 'table',
                id: index,
                propertyName: 'textEdit',
                old: textFocusSnapshotRef.current,
                new: currentSnapshot,
            });
        }
        textFocusSnapshotRef.current = null;
    };
    var addColumn = function (tableIndex, colIndex) {
        flushTextSnapshot();
        saveStateGuardRef.current = false;
        setTable(function (prev) {
            var oldDataStr = JSON.stringify(prev[tableIndex]);
            var newTable = __spreadArray([], prev, true);
            var tbl = __assign({}, newTable[tableIndex]);
            var data = __assign({}, tbl.data);
            var prevNumRows = data.rowsHeight.length;
            var newColWidth = __spreadArray([], data.colWidth, true);
            newColWidth.splice(colIndex + 1, 0, data.colWidth[0]);
            data.colWidth = newColWidth;
            var newNumCols = data.colWidth.length;
            var updatedCells = [];
            for (var r = 0; r < prevNumRows; r++) {
                for (var c = 0; c < newNumCols; c++) {
                    if (c === colIndex + 1) {
                        updatedCells.push({
                            type: 'text',
                            data: { text: '', color: ColorEnum[color], size: 30 },
                            cellId: generateCellId(),
                        });
                    }
                    else {
                        var oldIndex = r * (newNumCols - 1) + (c > colIndex ? c - 1 : c);
                        updatedCells.push(data.cellData[oldIndex]);
                    }
                }
            }
            data.cellData = updatedCells;
            data.dimensions = __assign(__assign({}, data.dimensions), { width: data.colWidth.reduce(function (a, b) { return a + b; }, 0) });
            tbl.data = data;
            newTable[tableIndex] = tbl;
            if (!saveStateGuardRef.current) {
                saveStateGuardRef.current = true;
                saveState({
                    type: 'table',
                    id: tableIndex,
                    propertyName: 'columnAdd',
                    old: oldDataStr,
                    new: JSON.stringify(tbl),
                });
            }
            return newTable;
        });
        announce(t('text.tableColumnAdded', { count: numCols + 1 }));
    };
    var removeColumn = function (tableIndex, colIndex) {
        flushTextSnapshot();
        saveStateGuardRef.current = false;
        setTable(function (prev) {
            var oldDataStr = JSON.stringify(prev[tableIndex]);
            var newTable = __spreadArray([], prev, true);
            var tbl = __assign({}, newTable[tableIndex]);
            var data = __assign({}, tbl.data);
            if (data.colWidth.length <= 1)
                return prev;
            var prevNumRows = data.rowsHeight.length;
            var newColWidth = __spreadArray([], data.colWidth, true);
            newColWidth.splice(colIndex, 1);
            data.colWidth = newColWidth;
            var newNumCols = data.colWidth.length;
            var updatedCells = [];
            for (var r = 0; r < prevNumRows; r++) {
                for (var c = 0; c < newNumCols; c++) {
                    var oldIndex = r * (newNumCols + 1) + (c >= colIndex ? c + 1 : c);
                    updatedCells.push(data.cellData[oldIndex]);
                }
            }
            data.cellData = updatedCells;
            data.dimensions = __assign(__assign({}, data.dimensions), { width: data.colWidth.reduce(function (a, b) { return a + b; }, 0) });
            tbl.data = data;
            newTable[tableIndex] = tbl;
            if (!saveStateGuardRef.current) {
                saveStateGuardRef.current = true;
                saveState({
                    type: 'table',
                    id: tableIndex,
                    propertyName: 'columnRemove',
                    old: oldDataStr,
                    new: JSON.stringify(tbl),
                });
            }
            return newTable;
        });
        // If focused cell was in the deleted column, move focus to a valid column
        var activeCell = selectedTool.find(function (id) { return id.startsWith("tableText_".concat(index, "_")); });
        if (activeCell) {
            var focusedCol = parseInt(activeCell.split('_')[3]);
            var newNumCols = colWidth.length - 1;
            if (focusedCol >= newNumCols) {
                var safeCol = Math.max(0, newNumCols - 1);
                var row = activeCell.split('_')[2];
                setSelectedTool(["tableText_".concat(index, "_").concat(row, "_").concat(safeCol)]);
            }
        }
        announce(t('text.tableColumnDeleted', { count: numCols - 1 }));
    };
    var addRow = function (tableIndex, rowIndex) {
        flushTextSnapshot();
        saveStateGuardRef.current = false;
        setTable(function (prev) {
            var oldDataStr = JSON.stringify(prev[tableIndex]);
            var newTable = __spreadArray([], prev, true);
            var tbl = __assign({}, newTable[tableIndex]);
            var data = __assign({}, tbl.data);
            var newRowsHeight = __spreadArray([], data.rowsHeight, true);
            newRowsHeight.splice(rowIndex + 1, 0, data.rowsHeight[0] || 90);
            data.rowsHeight = newRowsHeight;
            var prevNumCols = data.colWidth.length;
            var emptyRow = Array(prevNumCols)
                .fill(0)
                .map(function () { return ({
                type: 'text',
                data: { text: '', color: ColorEnum[color], size: 30 },
                cellId: generateCellId(),
            }); });
            var insertAt = (rowIndex + 1) * prevNumCols;
            var updated = __spreadArray([], data.cellData, true);
            updated.splice.apply(updated, __spreadArray([insertAt, 0], emptyRow, false));
            data.cellData = updated;
            data.dimensions = __assign(__assign({}, data.dimensions), { height: data.rowsHeight.reduce(function (a, b) { return a + b; }, 0) });
            tbl.data = data;
            newTable[tableIndex] = tbl;
            if (!saveStateGuardRef.current) {
                saveStateGuardRef.current = true;
                saveState({
                    type: 'table',
                    id: tableIndex,
                    propertyName: 'rowAdd',
                    old: oldDataStr,
                    new: JSON.stringify(tbl),
                });
            }
            return newTable;
        });
        announce(t('text.tableRowAdded', { count: numRows + 1 }));
    };
    var removeRow = function (tableIndex, rowIndex) {
        flushTextSnapshot();
        saveStateGuardRef.current = false;
        setTable(function (prev) {
            var oldDataStr = JSON.stringify(prev[tableIndex]);
            var newTable = __spreadArray([], prev, true);
            var tbl = __assign({}, newTable[tableIndex]);
            var data = __assign({}, tbl.data);
            if (data.rowsHeight.length <= 1)
                return prev;
            var prevNumCols = data.colWidth.length;
            var newRowsHeight = __spreadArray([], data.rowsHeight, true);
            newRowsHeight.splice(rowIndex, 1);
            data.rowsHeight = newRowsHeight;
            var start = rowIndex * prevNumCols;
            var newCellData = __spreadArray([], data.cellData, true);
            newCellData.splice(start, prevNumCols);
            data.cellData = newCellData;
            data.dimensions = __assign(__assign({}, data.dimensions), { height: data.rowsHeight.reduce(function (a, b) { return a + b; }, 0) });
            tbl.data = data;
            newTable[tableIndex] = tbl;
            if (!saveStateGuardRef.current) {
                saveStateGuardRef.current = true;
                saveState({
                    type: 'table',
                    id: tableIndex,
                    propertyName: 'rowRemove',
                    old: oldDataStr,
                    new: JSON.stringify(tbl),
                });
            }
            return newTable;
        });
        // If focused cell was in the deleted row, move focus to a valid row
        var activeCell = selectedTool.find(function (id) { return id.startsWith("tableText_".concat(index, "_")); });
        if (activeCell) {
            var focusedRow = parseInt(activeCell.split('_')[2]);
            var newNumRows = rowsHeight.length - 1;
            if (focusedRow >= newNumRows) {
                var safeRow = Math.max(0, newNumRows - 1);
                var col = activeCell.split('_')[3];
                setSelectedTool(["tableText_".concat(index, "_").concat(safeRow, "_").concat(col)]);
            }
        }
        announce(t('text.tableRowDeleted', { count: numRows - 1 }));
    };
    // The Controller function that was causing your error
    var controller = function (tableIdx, colRowIdx, type, from) {
        if (from === 'row') {
            type === 'add' ? addRow(tableIdx, colRowIdx) : removeRow(tableIdx, colRowIdx);
        }
        else {
            type === 'add' ? addColumn(tableIdx, colRowIdx) : removeColumn(tableIdx, colRowIdx);
        }
    };
    /** ---------------- UI POSITIONING ---------------- */
    var getColumnMid = function (colIndex) {
        if (!tableRef.current)
            return { left: 0 };
        var cells = tableRef.current.querySelectorAll('tr:first-child td');
        var cell = cells[colIndex];
        if (!cell)
            return { left: 0 };
        var rect = cell.getBoundingClientRect();
        var tableRect = tableRef.current.getBoundingClientRect();
        return { left: (rect.left - tableRect.left) / zoomLevel + rect.width / zoomLevel / 2 };
    };
    var getRowMid = function (rowIndex) {
        if (!tableRef.current)
            return { top: 0, left: 0 };
        var rows = tableRef.current.querySelectorAll('tr');
        var row = rows[rowIndex];
        if (!row)
            return { top: 0, left: 0 };
        var rect = row.getBoundingClientRect();
        var tableRect = tableRef.current.getBoundingClientRect();
        return {
            top: (rect.top - tableRect.top) / zoomLevel + rect.height / zoomLevel / 2,
            left: tableRect.width / zoomLevel + 10,
        };
    };
    useLayoutEffect(function () {
        if (!tableRef.current)
            return;
        var cols = [];
        for (var i = 0; i < numCols; i++)
            cols.push(getColumnMid(i));
        setColPos(cols);
        var rows = [];
        for (var i = 0; i < numRows; i++)
            rows.push(getRowMid(i));
        setRowPos(rows);
    }, [tabledata, zoomLevel, numRows, numCols, localTablePos]);
    /** ---------------- PICKER HELPERS (OnCanvasPickerPattern) ---------------- */
    var handlePickerCellActivate = function (r, c, btnEl, pos) {
        var _a, _b;
        pickerCellRef.current = { r: r, c: c };
        if (!selectedTool.includes("tableText_".concat(index)))
            setSelectedTool(["tableText_".concat(index)]);
        var synthId = "tableText_".concat(index, "_cell_").concat(r, "_").concat(c);
        var rect = btnEl.getBoundingClientRect();
        setContextMenu({ x: (_a = pos === null || pos === void 0 ? void 0 : pos.x) !== null && _a !== void 0 ? _a : rect.right, y: (_b = pos === null || pos === void 0 ? void 0 : pos.y) !== null && _b !== void 0 ? _b : rect.top, manipulativeId: synthId });
    };
    var handleContainerKeyDown = function (e) {
        var _a;
        if (pickerActive)
            return; // picker buttons handle their own key events
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            e.stopPropagation();
            setSelectedTool(["tableText_".concat(index)]);
            var rect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect)
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: "tableText_".concat(index) });
        }
    };
    var handleContainerClick = function (e) {
        var _a;
        // If onMouseDown already ran cell-detection for this event (trackpad or desktop
        // click), skip context menu — the mousedown path handles focus/edit-mode.
        // Only open context menu for VO synthetic clicks (no preceding mousedown) or
        // keyboard Enter/Space (detail === 0).
        if (onMouseDownHandledRef.current) {
            onMouseDownHandledRef.current = false;
            return;
        }
        if (e.detail !== 0)
            return;
        e.preventDefault();
        setSelectedTool(["tableText_".concat(index)]);
        var rect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (rect)
            setContextMenu({ x: rect.right, y: rect.top, manipulativeId: "tableText_".concat(index) });
    };
    /** ---------------- EVENT HANDLERS ---------------- */
    var onMouseDown = function (e) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (panningEnable)
            return;
        // Right-click is handled entirely by onContextMenu — skip focus logic here
        if ('button' in e && e.button === 2)
            return;
        // Ignore events that bubbled in from a portaled MathEditor surface (math
        // keypad, color picker, link popup, etc.). Those elements live in
        // document.body but remain React-tree descendants of this container, so
        // their mousedown/touchstart bubbles here. In the DOM they are NOT inside
        // containerRef, so closest('td') below would return null and we'd wrongly
        // treat a keypad tap as a "click outside any cell" — exiting cell editing
        // and yanking focus to the sentinel after every keystroke.
        if (containerRef.current && !containerRef.current.contains(e.target)) {
            return;
        }
        // Row/column add-remove controls have no <td> under them, so without this
        // guard the branch below (isFocused && !clickedCellKey) treats every tap on
        // them as "touched a non-cell area" — exiting cell editing and moving focus
        // to the sentinel on touchstart, racing the button's own click (which fires
        // later, on touchend) and sometimes winning, so the tap does nothing.
        if (e.target.closest('.rowControlsContainer, .columnControlsContainer')) {
            return;
        }
        // When the sentinel button intercepts the initial click (table not yet focused),
        // e.target is the sentinel itself. Use elementFromPoint — temporarily hiding the
        // sentinel — to find the actual element underneath and detect the clicked cell.
        var target = e.target;
        if (target === sentinelBtnRef.current && sentinelBtnRef.current) {
            var clientX_1 = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY_1 = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            sentinelBtnRef.current.style.visibility = 'hidden';
            var elBelow = document.elementFromPoint(clientX_1, clientY_1);
            sentinelBtnRef.current.style.visibility = '';
            if (elBelow)
                target = elBelow;
        }
        // Detect which cell was clicked (used in both the focused and unfocused paths below).
        var td = target.closest('td');
        var clickedCellKey = null;
        if (td && tableRef.current) {
            var tbody = tableRef.current.querySelector('tbody');
            var row = td.closest('tr');
            if (tbody && row) {
                var rowIndex = Array.from(tbody.children).indexOf(row);
                var colIndex = Array.from(row.children).indexOf(td);
                if (rowIndex >= 0 && colIndex >= 0) {
                    clickedCellKey = "".concat(index, "_").concat(rowIndex, "_").concat(colIndex);
                }
            }
        }
        if (!isFocused) {
            // Only block programmatic focus when touch lands on the table container
            // (no specific cell). When a cell is tapped directly, we want the editor
            // to focus immediately so the user can type without a second tap.
            touchFocusRef.current = ('touches' in e) && !clickedCellKey;
            if ('touches' in e)
                ignoreNextTabShiftRef.current = true;
            // Sentinel touch guard: skip for actual finger touches only.
            // Trackpad/pointer clicks on HAS_TOUCH devices must fall through so
            // onMouseDown can run cell detection — those are NOT VO synthetic clicks.
            // The native handleTouchStartDrag listener calls e.preventDefault() on real
            // touch events, suppressing the subsequent click so handleContainerClick
            // never fires for normal finger taps anyway.
            if (e.target === sentinelBtnRef.current && 'touches' in e) {
                return;
            }
            // TalkBack (and other AT) double-tap fires a synthetic mousedown with detail=0,
            // not a real touchstart. Return early so the subsequent synthetic click fires
            // handleContainerClick (which opens the table context menu). Setting
            // onMouseDownHandledRef here would cause handleContainerClick to skip the menu.
            if (!('touches' in e) && e.detail === 0) {
                return;
            }
            // Signal to handleContainerClick that a real mousedown ran here.
            // handleContainerClick will skip context menu when this is true — the
            // mousedown path already handled cell detection / focus.
            onMouseDownHandledRef.current = true;
            // Firefox-specific race condition fix:
            // Firefox fires `focus` synchronously during `mousedown` bubbling. MathEditor's
            // onFocus → setSelectedTool causes an immediate React re-render that mutates
            // the DOM (e.g. adds the 'focused' className to the contenteditable) BEFORE
            // the browser finalises cursor placement — Firefox loses the caret.
            // Chrome/Safari are immune; they preserve the caret through DOM mutations.
            //
            // Fix: preventDefault() takes control away from the browser's natural focus.
            // No focus event fires, no premature React re-render. Instead, setSelectedTool
            // is deferred (setTimeout 0), and the cursor is placed programmatically in the
            // useEffect below — AFTER React's render cycle and all DOM mutations are done.
            e.preventDefault();
            if (clickedCellKey) {
                // Check if the clicked cell is editing-locked (viewonly).
                // Locked cells have contentEditable=false — there is no caret to place, so the
                // Firefox setTimeout workaround is unnecessary. Update selectedTool synchronously
                // so the sidebar lock icon reflects the correct cell state in the very first render
                // (no async gap where selectedTool=[] causes a stale "unlock" icon).
                var parts = clickedCellKey.split('_');
                var rIdx = parseInt(parts[1]);
                var cIdx = parseInt(parts[2]);
                var isLockedCell = ((_c = (_b = (_a = cellData[rIdx * numCols + cIdx]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing) === true;
                // Notify toolbar of this cell's content-lock state synchronously, in the same
                // React event batch as setSelectedTool, so the lock icon updates in one render.
                customEventHandler.dispatchEvent('tableCellLockState', { locked: isLockedCell });
                if (isLockedCell) {
                    pendingFocusRef.current = null;
                    setSelectedTool(["tableText_".concat(clickedCellKey)]);
                }
                else {
                    pendingFocusRef.current = clickedCellKey;
                    setTimeout(function () {
                        setSelectedTool(["tableText_".concat(clickedCellKey)]);
                    }, 0);
                }
            }
            else {
                // Touch on non-cell area (e.g. drag handle) — focus table without entering any cell
                pendingFocusRef.current = null;
                setTimeout(function () { return setSelectedTool(["tableText_".concat(index)]); }, 0);
            }
        }
        else if (isFocused && !clickedCellKey) {
            // Drag handle (or other non-cell area) while focused — exit cell editing mode.
            // If this table is already part of a broader multi-select (other manipulatives
            // selected alongside it), preserve that selection instead of collapsing it down
            // to just this table — otherwise clicking the drag handle to lead a multi-select
            // drag wiped every other selected item right at mousedown.
            var alreadyInBroaderSelection = selectedTool.length > 1 && selectedTool.includes("tableText_".concat(index));
            if (!alreadyInBroaderSelection) {
                setSelectedTool(["tableText_".concat(index)]);
            }
            (_d = sentinelBtnRef.current) === null || _d === void 0 ? void 0 : _d.focus();
        }
        else if (!isCellSelectMode && clickedCellKey) {
            // On touch devices the picker button's onClick owns cell interaction (opens context
            // menu). Skip for real touch events (touchstart falling through pointer-events:none
            // sentinel) AND for AT synthetic mousedowns (TalkBack detail=0). In both cases the
            // subsequent click fires handlePickerCellActivate or handleContainerClick correctly.
            if ('touches' in e) {
                // iPadOS: first Tab after a tap fires with spurious shiftKey=true — set the flag
                // even though we skip the rest of this handler (picker button onClick handles it).
                ignoreNextTabShiftRef.current = true;
                return;
            }
            if (e.detail === 0)
                return;
            // Picker buttons own their own click interaction on desktop too.
            if (target.closest('.tableCellPickerBtn'))
                return;
            // Table is already focused. When user clicks a locked/viewonly cell, MathEditor's
            // onFocus does not fire (contenteditable="false"), so selectedTool stays on the
            // previously active cell. Explicitly update selectedTool here so the lock icon
            // correctly reflects the clicked cell's editing-lock state.
            var newTool = "tableText_".concat(clickedCellKey);
            var clickedParts = clickedCellKey.split('_');
            var clickedRIdx = parseInt(clickedParts[1]);
            var clickedCIdx = parseInt(clickedParts[2]);
            var isClickedCellLocked = ((_g = (_f = (_e = cellData[clickedRIdx * numCols + clickedCIdx]) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.lock) === null || _g === void 0 ? void 0 : _g.editing) === true;
            // Notify toolbar synchronously so lock icon reflects the newly clicked cell.
            customEventHandler.dispatchEvent('tableCellLockState', { locked: isClickedCellLocked });
            if (selectedTool.length !== 1 || selectedTool[0] !== newTool) {
                setSelectedTool([newTool]);
            }
            // Touch tap inside a math-field (e.g. a fraction numerator/denominator
            // placeholder): let MathLive own the caret placement and bail out of the
            // table handler entirely. If we don't:
            //   - force-focusing the parent editorDiv rips the caret out of the
            //     placeholder (within-cell "jump"), and
            //   - arming the table drag below makes the global non-passive touchmove
            //     handler preventDefault() the gesture, blocking MathLive's native tap
            //     so focus falls through to the blank canvas (iPad).
            // selectedTool was already updated above; MathEditor's own focus handler
            // keeps the cell selection in sync when the math-field focuses.
            if ('touches' in e && target.closest('math-field')) {
                ignoreNextTabShiftRef.current = true;
                return;
            }
            // Touch: focus the editor synchronously within the iOS user-activation window
            // so the keyboard opens. The pendingFocusRef/useEffect path only fires from
            // the !isFocused path; we must call focus() directly here.
            if ('touches' in e && !isClickedCellLocked) {
                var tbody = (_h = tableRef.current) === null || _h === void 0 ? void 0 : _h.querySelector('tbody');
                var tRow = tbody ? Array.from(tbody.children)[clickedRIdx] : null;
                var tCell = tRow ? Array.from(tRow.children)[clickedCIdx] : null;
                (_j = tCell === null || tCell === void 0 ? void 0 : tCell.querySelector('.editorDiv')) === null || _j === void 0 ? void 0 : _j.focus({ preventScroll: true });
                // iPadOS software keyboard bug: first Tab after a tap fires with shiftKey=true.
                ignoreNextTabShiftRef.current = true;
            }
        }
        if (isPositionLocked)
            return;
        if (isCellSelectMode)
            return;
        if (target.closest('.row-resizer, .col-resizer, .resize-handle, .rowControlsContainer, .columnControlsContainer, .keypadWrapper, .tableBottomUIWrapper') ||
            ['INPUT', 'TEXTAREA'].includes(target.tagName) ||
            // Never start a table drag from inside an editable cell — on touch this
            // was previously allowed, so on iPad dragging across text to select (or a
            // double-tap to select a word) armed the table drag instead: the global
            // non-passive touchmove handler then moved the table and preventDefault'd
            // the gesture, breaking selection and leaking highlights outside the table.
            // Editing/selecting happens inside the cell; dragging the table uses its
            // border/chrome or the move handle (neither is contentEditable).
            target.closest('[contenteditable="true"], math-field'))
            return;
        if ('touches' in e && !target.closest('[contenteditable="true"]'))
            e.preventDefault();
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        tablePosRef.current = __assign({}, localTablePosRef.current); // Snapshot current pos as drag anchor
        dragStartRef.current = { x: clientX, y: clientY };
        hasMoved.current = false;
        dragStated.current = true;
        setIsDragging(true);
        startMultiDrag();
    };
    useEffect(function () {
        var handleMove = function (e) {
            var _a, _b, _c, _d, _e;
            if (!dragStated.current || !dragStartRef.current)
                return;
            if ('touches' in e)
                e.preventDefault();
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var dx = clientX - dragStartRef.current.x;
            var dy = clientY - dragStartRef.current.y;
            if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) > 5)
                hasMoved.current = true;
            if (hasMoved.current) {
                var rawX = tablePosRef.current.x + dx / zoomLevel;
                var rawY = tablePosRef.current.y + dy / zoomLevel;
                var tw = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
                var th = (_d = (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.offsetHeight) !== null && _d !== void 0 ? _d : 0;
                // The drag handle is absolutely positioned to the left of the container
                // (left: -5px + translate(-100%)). Keep it inside the canvas by using
                // its offsetWidth + 5px as the minimum x.
                var handleEl = (_e = containerRef.current) === null || _e === void 0 ? void 0 : _e.querySelector('.tableMoveHandle');
                var minX = handleEl ? handleEl.offsetWidth + 5 : 0;
                var newX = Math.max(minX, Math.min(rawX, canvasWidth - tw));
                var newY = Math.max(0, Math.min(rawY, canvasHeight - th));
                setLocalTablePos({
                    x: newX,
                    y: newY,
                    z: tablePosRef.current.z,
                });
                moveMultiDrag(newX - tablePosRef.current.x, newY - tablePosRef.current.y);
            }
        };
        var stopDrag = function () {
            if (!dragStated.current)
                return;
            if (hasMoved.current) {
                var finalPos_1 = __assign({}, localTablePosRef.current);
                var entry = {
                    type: 'table',
                    id: index,
                    propertyName: 'position',
                    old: JSON.stringify(tablePosRef.current),
                    new: JSON.stringify(finalPos_1),
                };
                if (!registerMultiSelectSaveEntries(tableSelectionId, [entry])) {
                    saveState(entry);
                }
                setTable(function (prev) {
                    var nt = __spreadArray([], prev, true);
                    nt[index] = __assign(__assign({}, nt[index]), { data: __assign(__assign({}, nt[index].data), { position: finalPos_1 }) });
                    return nt;
                });
            }
            dragStated.current = false;
            dragStartRef.current = null;
            setIsDragging(false);
            endMultiDrag();
        };
        // Native non-passive touchstart on the container: React 17+ registers onTouchStart as
        // passive (e.preventDefault() is a no-op there), so iOS treats the touch as a potential
        // scroll and defers drag recognition. This native listener fires before React's handler
        // and can actually cancel the scroll, allowing immediate drag initiation.
        var el = containerRef.current;
        var handleTouchStartDrag = function (e) {
            var target = e.target;
            // Skip control areas and editable cells (they have their own touch behaviour).
            // Note: only skip [contenteditable="true"] (unlocked/editable cells), NOT all
            // math-field elements. Locked cells have contenteditable="false" and should NOT
            // skip preventDefault — otherwise iOS treats the touch as a scroll and the table
            // drag fails intermittently on iPad.
            if (target.closest('[contenteditable="true"], .row-resizer, .col-resizer, .resize-handle, .rowControlsContainer, .columnControlsContainer, .keypadWrapper, .tableBottomUIWrapper, .tableCellPickerBtn'))
                return;
            e.preventDefault(); // Prevents iOS from locking this gesture as a scroll
        };
        if (el)
            el.addEventListener('touchstart', handleTouchStartDrag, { passive: false });
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', stopDrag);
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', stopDrag);
        window.addEventListener('touchcancel', stopDrag);
        return function () {
            if (el)
                el.removeEventListener('touchstart', handleTouchStartDrag);
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', stopDrag);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', stopDrag);
            window.removeEventListener('touchcancel', stopDrag);
        };
        // moveMultiDrag/endMultiDrag close over `selectedTool` (via useMultiSelectDragSource) to
        // decide whether to broadcast at all — without selectedTool in these deps, handleMove/
        // stopDrag kept a STALE closure from whenever this effect last ran, so moveMultiDrag's
        // own willBroadcast check evaluated against old selection state and silently never
        // dispatched 'multiSelectMove', even though startMultiDrag (called fresh inside
        // onMouseDown) correctly saw the current selection and fired 'multiSelectMoveStart'.
    }, [zoomLevel, index, setTable, selectedTool, moveMultiDrag, endMultiDrag]);
    // MULTI-SELECT FOLLOWER — move this table when some OTHER selected item (of any
    // type) is dragged. The table lives in its own `table` array, not `manipulative`,
    // so the central useMultiSelectFollowerEngine can never reach it — mirrors the
    // same generic inMultiSel pattern used by Shades.tsx/ColoredTiles/LinearPiece.
    useEffect(function () {
        var isMultiSelected = selectedTool.includes(tableSelectionId) && selectedTool.length > 1;
        if (!isMultiSelected)
            return;
        var myStart = __assign({}, localTablePosRef.current);
        var followerOldPos = null;
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === tableSelectionId)
                return;
            myStart = __assign({}, localTablePosRef.current);
            followerOldPos = __assign({}, localTablePosRef.current);
        };
        var onMove = function (_a) {
            var sourceId = _a.sourceId, deltaX = _a.deltaX, deltaY = _a.deltaY;
            if (sourceId === tableSelectionId || isPositionLocked)
                return;
            var inMultiSel = selectedToolRef.current.includes(tableSelectionId) && selectedToolRef.current.length > 1;
            if (!inMultiSel)
                return;
            setLocalTablePos({ x: myStart.x + deltaX, y: myStart.y + deltaY, z: myStart.z });
        };
        var onEnd = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === tableSelectionId)
                return;
            if (followerOldPos) {
                var finalPos_2 = __assign({}, localTablePosRef.current);
                var entry = {
                    type: 'table',
                    id: index,
                    propertyName: 'position',
                    old: JSON.stringify(followerOldPos),
                    new: JSON.stringify(finalPos_2),
                };
                if (!registerMultiSelectSaveEntries(sourceId, [entry])) {
                    saveState(entry);
                }
                setTable(function (prev) {
                    var nt = __spreadArray([], prev, true);
                    nt[index] = __assign(__assign({}, nt[index]), { data: __assign(__assign({}, nt[index].data), { position: finalPos_2 }) });
                    return nt;
                });
            }
            followerOldPos = null;
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTool, isPositionLocked, index]);
    /** ---------------- RESIZE ---------------- */
    var onResizeStart = function (e, handle) {
        e.stopPropagation();
        e.preventDefault();
        if (panningEnable || isPositionLocked)
            return;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        resizeHandleRef.current = handle;
        resizeStartRef.current = {
            mouseX: clientX,
            mouseY: clientY,
            width: colWidth.reduce(function (a, b) { return a + b; }, 0),
            height: rowsHeight.reduce(function (a, b) { return a + b; }, 0),
            posX: localTablePos.x,
            posY: localTablePos.y,
            colWidths: __spreadArray([], colWidth, true),
            rowHeights: __spreadArray([], rowsHeight, true),
            cellSizes: cellData.map(function (c) { var _a; return ((_a = c.data) === null || _a === void 0 ? void 0 : _a.size) || 25; }),
        };
        resizeOldSnapshotRef.current = JSON.stringify(tabledata);
    };
    useEffect(function () {
        var handleResizeMove = function (e) {
            if (!resizeHandleRef.current || !resizeStartRef.current)
                return;
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var dx = (clientX - resizeStartRef.current.mouseX) / zoomLevel;
            var dy = (clientY - resizeStartRef.current.mouseY) / zoomLevel;
            var nw = resizeStartRef.current.width, nh = resizeStartRef.current.height, nx = resizeStartRef.current.posX, ny = resizeStartRef.current.posY;
            var h = resizeHandleRef.current;
            if (h.includes('e'))
                nw = Math.max(100, nw + dx);
            if (h.includes('w')) {
                var dw = Math.max(100, nw - dx) - nw;
                nw += dw;
                nx -= dw;
            }
            if (h.includes('s'))
                nh = Math.max(50, nh + dy);
            if (h.includes('n')) {
                var dh = Math.max(50, nh - dy) - nh;
                nh += dh;
                ny -= dh;
            }
            var sx = nw / resizeStartRef.current.width, sy = nh / resizeStartRef.current.height;
            var textScale = (sx + sy) / 2;
            var scw = resizeStartRef.current.colWidths.map(function (w) { return Math.max(60, w * sx); }), srh = resizeStartRef.current.rowHeights.map(function (h) { return Math.max(60, h * sy); });
            var origSizes = resizeStartRef.current.cellSizes;
            setTable(function (prev) {
                var nt = __spreadArray([], prev, true);
                var td = __assign({}, nt[index].data);
                td.colWidth = scw;
                td.rowsHeight = srh;
                td.dimensions = {
                    width: scw.reduce(function (a, b) { return a + b; }, 0),
                    height: srh.reduce(function (a, b) { return a + b; }, 0),
                };
                td.position = { x: nx, y: ny, z: localTablePos.z };
                td.cellData = td.cellData.map(function (cell, i) { return (__assign(__assign({}, cell), { data: __assign(__assign({}, cell.data), { size: Math.max(10, Math.round(origSizes[i] * textScale)) }) })); });
                nt[index] = __assign(__assign({}, nt[index]), { data: td });
                return nt;
            });
            setLocalTablePos({ x: nx, y: ny, z: localTablePos.z });
            updateToolbarPosition();
        };
        var stop = function () {
            if (resizeHandleRef.current && resizeOldSnapshotRef.current) {
                var newSnap = JSON.stringify(tabledataRef.current);
                saveState({
                    type: 'table',
                    id: index,
                    propertyName: 'resize',
                    old: resizeOldSnapshotRef.current,
                    new: newSnap,
                });
            }
            resizeHandleRef.current = null;
            resizeStartRef.current = null;
            resizeOldSnapshotRef.current = null;
        };
        window.addEventListener('mousemove', handleResizeMove);
        window.addEventListener('mouseup', stop);
        window.addEventListener('touchmove', handleResizeMove, { passive: false });
        window.addEventListener('touchend', stop);
        window.addEventListener('touchcancel', stop);
        return function () {
            window.removeEventListener('mousemove', handleResizeMove);
            window.removeEventListener('mouseup', stop);
            window.removeEventListener('touchmove', handleResizeMove);
            window.removeEventListener('touchend', stop);
            window.removeEventListener('touchcancel', stop);
        };
    }, [zoomLevel, index, setTable, localTablePos.z, updateToolbarPosition]);
    /** ---------------- GLOBAL EVENTS ---------------- */
    useEffect(function () {
        var handleCellDuplicate = function (data) {
            if (data.index !== index || !isCellSelectMode || selectedCells.size === 0)
                return;
            setSkipTableDuplicate(true);
            var uniqueRows = new Set();
            var uniqueCols = new Set();
            selectedCells.forEach(function (cellKey) {
                var _a = cellKey.split('_').map(Number), r = _a[0], c = _a[1];
                uniqueRows.add(r);
                uniqueCols.add(c);
            });
            var duplicateRows = uniqueRows.size < numRows || uniqueCols.size === numCols;
            var duplicateCols = uniqueCols.size < numCols && uniqueRows.size === numRows;
            var sortedRows = Array.from(uniqueRows).sort(function (a, b) { return a - b; }), sortedCols = Array.from(uniqueCols).sort(function (a, b) { return a - b; });
            var oldCD = JSON.stringify(tabledataRef.current);
            saveStateGuardRef.current = false;
            setTable(function (prev) {
                var nt = __spreadArray([], prev, true);
                var tbl = __assign({}, nt[index]);
                var td = __assign({}, tbl.data);
                var ccd = __spreadArray([], td.cellData, true), ncw = td.colWidth.length;
                if (duplicateRows) {
                    sortedRows.forEach(function (sr) {
                        td.rowsHeight = __spreadArray(__spreadArray([], td.rowsHeight, true), [td.rowsHeight[sr] || 90], false);
                        var rc = [];
                        for (var c = 0; c < ncw; c++)
                            rc.push(JSON.parse(JSON.stringify(ccd[sr * ncw + c])));
                        ccd = __spreadArray(__spreadArray([], ccd, true), rc, true);
                    });
                    td.dimensions = __assign(__assign({}, td.dimensions), { height: td.rowsHeight.reduce(function (a, b) { return a + b; }, 0) });
                }
                if (duplicateCols) {
                    sortedCols.forEach(function (sc) {
                        td.colWidth = __spreadArray(__spreadArray([], td.colWidth, true), [td.colWidth[sc]], false);
                        var nnc = td.colWidth.length;
                        var rbc = [];
                        for (var r = 0; r < td.rowsHeight.length; r++) {
                            for (var c = 0; c < ncw; c++)
                                rbc.push(ccd[r * ncw + c]);
                            rbc.push(JSON.parse(JSON.stringify(ccd[r * ncw + sc])));
                        }
                        ccd = rbc;
                        ncw = nnc;
                    });
                    td.dimensions = __assign(__assign({}, td.dimensions), { width: td.colWidth.reduce(function (a, b) { return a + b; }, 0) });
                }
                td.cellData = ccd;
                tbl.data = td;
                nt[index] = tbl;
                if (!saveStateGuardRef.current) {
                    saveStateGuardRef.current = true;
                    saveState({
                        type: 'table',
                        id: index,
                        propertyName: 'cellDuplicate',
                        old: oldCD,
                        new: JSON.stringify(tbl),
                    });
                }
                return nt;
            });
            setIsCellSelectMode(false);
            setSelectedCells(new Set());
        };
        var handleBP = function (data) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
            if (data.id === 'mathtext') {
                // Close any open toolbar collapsible (e.g. thickness) before opening keypad
                var toolbar_1 = document.querySelector('.bottomPanelTool');
                if (toolbar_1) {
                    var expandedHolder = toolbar_1.querySelector('.expanded_bottom, .expanding_bottom');
                    if (expandedHolder) {
                        var container = expandedHolder.parentElement;
                        var toggleBtn = container === null || container === void 0 ? void 0 : container.querySelector('.collapsibleMainBtn');
                        toggleBtn === null || toggleBtn === void 0 ? void 0 : toggleBtn.click();
                    }
                }
                // Insert math field in the active cell's MathEditor (same pattern as Dice)
                var activeCell = selectedTool.find(function (id) { return id.startsWith("tableText_".concat(index, "_")); });
                if (activeCell) {
                    var cellKey_1 = activeCell.replace('tableText_', '');
                    setTimeout(function () {
                        var cellEditor = mathEditorRefs.current[cellKey_1];
                        // MathEditor's own 'bottomPanel' listener has usually already
                        // handled this press (it is registered whenever the cell editor is
                        // the active selection); this delayed call is the fallback for the
                        // frame where that listener isn't mounted yet. openKeypad() reports
                        // true both when the keypad is already open and when it reopened on
                        // the caret's expression — either way inserting here would add a
                        // stray empty math-field to the cell.
                        if (cellEditor === null || cellEditor === void 0 ? void 0 : cellEditor.openKeypad())
                            return;
                        cellEditor === null || cellEditor === void 0 ? void 0 : cellEditor.insertMath();
                    }, 10);
                }
            }
            if (!isFocused)
                return;
            if (data.id === 'tableTab') {
                var activeCell = selectedToolRef.current.find(function (id) { return id.startsWith("tableText_".concat(index, "_")) && id.split('_').length === 4; });
                if (!activeCell)
                    return;
                var _q = activeCell.split('_'), rowStr = _q[2], colStr = _q[3];
                var row = parseInt(rowStr);
                var col = parseInt(colStr);
                var cols = tabledataRef.current.data.colWidth.length;
                var rows = tabledataRef.current.data.rowsHeight.length;
                var nextCol = col + 1;
                var nextRow = row;
                if (nextCol >= cols) {
                    nextCol = 0;
                    nextRow = row + 1;
                }
                if (nextRow >= rows) {
                    nextRow = 0;
                    nextCol = 0;
                }
                setSelectedTool(["tableText_".concat(index, "_").concat(nextRow, "_").concat(nextCol)]);
                announce(t('text.tableTabAnnounce', { row: nextRow + 1, col: nextCol + 1 }));
                // Focus the next cell synchronously, in this same click handler, in addition to
                // (not instead of) the pendingFocusRef/useEffect path below. On iOS Safari,
                // .focus() on a contenteditable only reliably opens the keyboard/commits focus
                // when called inside the original tap's user-activation window — deferring
                // past it made this button's focus sporadic on iPad. The target cell already
                // exists in the DOM (Tab only navigates, never creates rows/columns), so it's
                // safe to query and focus it directly here. pendingFocusRef is still set below
                // so the useEffect's later re-focus (its documented "final word" safety net for
                // MathEditor child effects stealing focus after mount, e.g. on Firefox) still
                // runs as a backstop — it's a harmless no-op re-focus when this already worked.
                var tbody = (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('tbody');
                var rowEl = tbody ? Array.from(tbody.children)[nextRow] : null;
                var cellTd = rowEl ? Array.from(rowEl.children)[nextCol] : null;
                var editorEl = cellTd === null || cellTd === void 0 ? void 0 : cellTd.querySelector('.editorDiv');
                if (editorEl) {
                    editorEl.focus({ preventScroll: true });
                    var sel = window.getSelection();
                    if (sel) {
                        var range = document.createRange();
                        range.selectNodeContents(editorEl);
                        range.collapse(false);
                        sel.removeAllRanges();
                        sel.addRange(range);
                    }
                }
                touchFocusRef.current = false;
                pendingFocusRef.current = "".concat(index, "_").concat(nextRow, "_").concat(nextCol);
                return;
            }
            if (data.id === 'select') {
                if (document.activeElement instanceof HTMLElement)
                    document.activeElement.blur();
                var entering_1 = !isCellSelectMode;
                setIsCellSelectMode(entering_1);
                if (!entering_1)
                    setSelectedCells(new Set());
                setTable(function (prev) {
                    var nt = __spreadArray([], prev, true);
                    nt[index] = __assign(__assign({}, nt[index]), { data: __assign(__assign({}, nt[index].data), { cellSelectMode: entering_1 }) });
                    return nt;
                });
                setSelectedTool(["tableText_".concat(index)]);
                if (entering_1) {
                    setTimeout(function () {
                        var _a, _b;
                        (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[data-picker-cell="0_0"]')) === null || _b === void 0 ? void 0 : _b.focus();
                    }, 0);
                }
                return;
            }
            if (data.id === 'thickness' ||
                data.id === 'thicknessCollapsible' ||
                data.id === 'tableThickness') {
                var nbw_1 = (_b = [2, 3, 5, 6][data.data]) !== null && _b !== void 0 ? _b : 2;
                var oldBW_1 = JSON.stringify(tabledataRef.current);
                saveStateGuardRef.current = false;
                setTable(function (prev) {
                    var nt = __spreadArray([], prev, true);
                    if (isCellSelectMode && selectedCells.size > 0) {
                        // Apply per-cell border width
                        var td = __assign({}, nt[index].data);
                        var ncd_1 = __spreadArray([], td.cellData, true);
                        selectedCells.forEach(function (ck) {
                            var _a = ck.split('_').map(Number), r = _a[0], c = _a[1];
                            var ci = r * numCols + c;
                            if (ncd_1[ci])
                                ncd_1[ci] = __assign(__assign({}, ncd_1[ci]), { data: __assign(__assign({}, ncd_1[ci].data), { borderWidth: nbw_1 }) });
                        });
                        td.cellData = ncd_1;
                        nt[index] = __assign(__assign({}, nt[index]), { data: td });
                    }
                    else {
                        var activeCell = selectedToolRef.current.find(function (t) { return t.startsWith("tableText_".concat(index, "_")) && t.split('_').length === 4; });
                        if (activeCell) {
                            // A specific cell is focused — apply border width only to that cell
                            var _a = activeCell.split('_').map(Number), r = _a[2], c = _a[3];
                            var td = __assign({}, nt[index].data);
                            var ncd = __spreadArray([], td.cellData, true);
                            var ci = r * numCols + c;
                            if (ncd[ci])
                                ncd[ci] = __assign(__assign({}, ncd[ci]), { data: __assign(__assign({}, ncd[ci].data), { borderWidth: nbw_1 }) });
                            td.cellData = ncd;
                            nt[index] = __assign(__assign({}, nt[index]), { data: td });
                        }
                        else {
                            // Table-level selection — apply border width to all cells + table default
                            var td = __assign({}, nt[index].data);
                            var ncd = td.cellData.map(function (c) { return (__assign(__assign({}, c), { data: __assign(__assign({}, c.data), { borderWidth: nbw_1 }) })); });
                            td.cellData = ncd;
                            td.borderWidth = nbw_1;
                            nt[index] = __assign(__assign({}, nt[index]), { data: td });
                        }
                    }
                    if (!saveStateGuardRef.current) {
                        saveStateGuardRef.current = true;
                        saveState({
                            type: 'table',
                            id: index,
                            propertyName: 'borderWidth',
                            old: oldBW_1,
                            new: JSON.stringify(nt[index]),
                        });
                    }
                    return nt;
                });
                return;
            }
            // Bulk operations: when cells are selected OR table-level selection (apply to all cells)
            var isTableLevel = selectedToolRef.current.some(function (t) { return t === "tableText_".concat(index); });
            var activeCellId = !isCellSelectMode
                ? selectedToolRef.current.find(function (id) { return id.startsWith("tableText_".concat(index, "_")) && id.split('_').length >= 4; })
                : undefined;
            var applyToAll = isCellSelectMode ? selectedCells.size === 0 : isTableLevel;
            if (applyToAll || (isCellSelectMode && selectedCells.size > 0) || activeCellId) {
                var bulkOps_1 = {
                    bold: function (d) { return (__assign(__assign({}, d), { bold: !d.bold })); },
                    italic: function (d) { return (__assign(__assign({}, d), { italic: !d.italic })); },
                    underline: function (d) { return (__assign(__assign({}, d), { underline: !d.underline })); },
                    larger: function (d) { return (__assign(__assign({}, d), { size: Math.min(100, (d.size || 25) + 5) })); },
                    smaller: function (d) { return (__assign(__assign({}, d), { size: Math.max(15, (d.size || 25) - 5) })); },
                    leftAlign: function (d) { return (__assign(__assign({}, d), { alignment: 'left' })); },
                    centerAlign: function (d) { return (__assign(__assign({}, d), { alignment: 'center' })); },
                    rightAlign: function (d) { return (__assign(__assign({}, d), { alignment: 'right' })); },
                };
                if (bulkOps_1[data.id]) {
                    var oldSnap_1 = JSON.stringify(tabledataRef.current);
                    saveStateGuardRef.current = false;
                    setTable(function (prev) {
                        var _a, _b;
                        var nt = __spreadArray([], prev, true);
                        var td = __assign({}, nt[index].data);
                        var ncd = __spreadArray([], td.cellData, true);
                        if (applyToAll) {
                            // Apply to all cells
                            ncd.forEach(function (c, i) {
                                var _a, _b;
                                if (c && !((_b = (_a = c.data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.editing)) {
                                    ncd[i] = __assign(__assign({}, c), { data: bulkOps_1[data.id](c.data) });
                                }
                            });
                        }
                        else if (activeCellId) {
                            // Apply to single active cell
                            var parts = activeCellId.split('_');
                            var ci = Number(parts[2]) * numCols + Number(parts[3]);
                            if (ncd[ci] && !((_b = (_a = ncd[ci].data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.editing)) {
                                ncd[ci] = __assign(__assign({}, ncd[ci]), { data: bulkOps_1[data.id](ncd[ci].data) });
                            }
                        }
                        else {
                            // Apply to selected cells only
                            selectedCells.forEach(function (ck) {
                                var _a, _b;
                                var _c = ck.split('_').map(Number), r = _c[0], c = _c[1];
                                var ci = r * numCols + c;
                                if (ncd[ci] && !((_b = (_a = ncd[ci].data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.editing)) {
                                    ncd[ci] = __assign(__assign({}, ncd[ci]), { data: bulkOps_1[data.id](ncd[ci].data) });
                                }
                            });
                        }
                        td.cellData = ncd;
                        nt[index] = __assign(__assign({}, nt[index]), { data: td });
                        if (!saveStateGuardRef.current) {
                            saveStateGuardRef.current = true;
                            saveState({
                                type: 'table',
                                id: index,
                                propertyName: data.id,
                                old: oldSnap_1,
                                new: JSON.stringify(nt[index]),
                            });
                        }
                        return nt;
                    });
                    return;
                }
            }
            // Row/column operations dispatched from context menu (keyboard accessibility)
            if (data.id === 'tableEditCell') {
                if (data.manipulativeId !== "tableText_".concat(index))
                    return;
                setPickerActive(false);
                touchFocusRef.current = false;
                if (data.focusEditor !== false) {
                    pendingFocusRef.current = "".concat(index, "_").concat(data.row, "_").concat(data.col);
                }
                else {
                    suppressFocusRedirectRef.current = true;
                }
                setSelectedTool(["tableText_".concat(index, "_").concat(data.row, "_").concat(data.col)]);
                return;
            }
            if (data.id === 'tableAddRowAbove') {
                // No picker cell selected → insert at top (addRow uses rowIndex+1, so -1 → index 0)
                var r = (_d = (_c = pickerCellRef.current) === null || _c === void 0 ? void 0 : _c.r) !== null && _d !== void 0 ? _d : 0;
                addRow(index, r - 1);
                return;
            }
            if (data.id === 'tableAddRowBelow') {
                // No picker cell selected → insert at bottom
                var r = (_f = (_e = pickerCellRef.current) === null || _e === void 0 ? void 0 : _e.r) !== null && _f !== void 0 ? _f : numRows - 1;
                addRow(index, r);
                return;
            }
            if (data.id === 'tableDeleteRow') {
                if (numRows <= 1)
                    return;
                var r = (_h = (_g = pickerCellRef.current) === null || _g === void 0 ? void 0 : _g.r) !== null && _h !== void 0 ? _h : numRows - 1;
                removeRow(index, r);
                return;
            }
            if (data.id === 'tableAddColLeft') {
                // No picker cell selected → insert at leftmost column
                var c = (_k = (_j = pickerCellRef.current) === null || _j === void 0 ? void 0 : _j.c) !== null && _k !== void 0 ? _k : 0;
                addColumn(index, c - 1);
                return;
            }
            if (data.id === 'tableAddColRight') {
                // No picker cell selected → insert at rightmost column
                var c = (_m = (_l = pickerCellRef.current) === null || _l === void 0 ? void 0 : _l.c) !== null && _m !== void 0 ? _m : numCols - 1;
                addColumn(index, c);
                return;
            }
            if (data.id === 'tableDeleteCol') {
                if (numCols <= 1)
                    return;
                var c = (_p = (_o = pickerCellRef.current) === null || _o === void 0 ? void 0 : _o.c) !== null && _p !== void 0 ? _p : numCols - 1;
                removeColumn(index, c);
                return;
            }
            if (data.id === 'cellSelectMode') {
                var entering_2 = !isCellSelectMode;
                if (document.activeElement instanceof HTMLElement)
                    document.activeElement.blur();
                setIsCellSelectMode(entering_2);
                if (!entering_2)
                    setSelectedCells(new Set());
                setTable(function (prev) {
                    var nt = __spreadArray([], prev, true);
                    nt[index] = __assign(__assign({}, nt[index]), { data: __assign(__assign({}, nt[index].data), { cellSelectMode: entering_2 }) });
                    return nt;
                });
                setSelectedTool(["tableText_".concat(index)]);
                announce(t(entering_2 ? 'text.tableCellSelectModeOn' : 'text.tableCellSelectModeOff'));
                return;
            }
            // Border width shorthand IDs from context menu (bw2/bw3/bw5/bw6)
            if (['bw2', 'bw3', 'bw5', 'bw6'].includes(data.id)) {
                var nbwMap = { bw2: 2, bw3: 3, bw5: 5, bw6: 6 };
                var nbw_2 = nbwMap[data.id];
                var oldBW_2 = JSON.stringify(tabledataRef.current);
                saveStateGuardRef.current = false;
                setTable(function (prev) {
                    var nt = __spreadArray([], prev, true);
                    var td = __assign({}, nt[index].data);
                    var ncd = td.cellData.map(function (c) { return (__assign(__assign({}, c), { data: __assign(__assign({}, c.data), { borderWidth: nbw_2 }) })); });
                    td.cellData = ncd;
                    td.borderWidth = nbw_2;
                    nt[index] = __assign(__assign({}, nt[index]), { data: td });
                    if (!saveStateGuardRef.current) {
                        saveStateGuardRef.current = true;
                        saveState({
                            type: 'table',
                            id: index,
                            propertyName: 'borderWidth',
                            old: oldBW_2,
                            new: JSON.stringify(nt[index]),
                        });
                    }
                    return nt;
                });
                return;
            }
            if (['scalePlus', 'scaleMinus'].includes(data.id)) {
                var oldScaleStr_1 = JSON.stringify(tabledataRef.current);
                saveStateGuardRef.current = false;
                setTable(function (prev) {
                    var _a;
                    var nt = __spreadArray([], prev, true);
                    var tbl = __assign({}, nt[index]);
                    var td = __assign({}, tbl.data);
                    var currentScale = (_a = td.scale) !== null && _a !== void 0 ? _a : 1;
                    var nextScale = data.id === 'scalePlus'
                        ? Math.min(5, Math.round((currentScale + 0.1) * 10) / 10)
                        : Math.max(0.5, Math.round((currentScale - 0.1) * 10) / 10);
                    if (nextScale === currentScale)
                        return prev;
                    var ratio = nextScale / currentScale;
                    td.colWidth = td.colWidth.map(function (w) { return Math.max(60, Math.round(w * ratio)); });
                    td.rowsHeight = td.rowsHeight.map(function (h) { return Math.max(60, Math.round(h * ratio)); });
                    td.cellData = td.cellData.map(function (cell) { return (__assign(__assign({}, cell), { data: __assign(__assign({}, cell.data), { size: Math.max(10, Math.round((cell.data.size || 25) * ratio)) }) })); });
                    td.dimensions = {
                        width: td.colWidth.reduce(function (a, b) { return a + b; }, 0),
                        height: td.rowsHeight.reduce(function (a, b) { return a + b; }, 0),
                    };
                    td.scale = nextScale;
                    tbl.data = td;
                    nt[index] = tbl;
                    if (!saveStateGuardRef.current) {
                        saveStateGuardRef.current = true;
                        saveState({
                            type: 'table',
                            id: index,
                            propertyName: 'scale',
                            old: oldScaleStr_1,
                            new: JSON.stringify(tbl),
                        });
                    }
                    return nt;
                });
            }
        };
        // A prior "select this text, change its color" pass wraps that run in
        // `{ type: 'html', value: '<span style="color:...">...</span>' }`. Applying
        // a new color at the cell/table level only updates the cell's default
        // `data.color`, which inline styles always win over — so those runs never
        // visually changed. Strip the inline color so the run falls back to the
        // new default.
        var stripInlineColor = function (text) {
            if (!Array.isArray(text))
                return text;
            return text.map(function (item) {
                return (item === null || item === void 0 ? void 0 : item.type) === 'html' && typeof item.value === 'string'
                    ? __assign(__assign({}, item), { value: item.value.replace(/color\s*:\s*[^;"]+;?/gi, '') }) : item;
            });
        };
        var handleCC = function (p) {
            if (!isFocused)
                return;
            if (isCellSelectMode) {
                // Cell-select mode: change background color of selected cells
                if (selectedCells.size === 0)
                    return;
                var oldCC_1 = JSON.stringify(tabledataRef.current);
                saveStateGuardRef.current = false;
                setTable(function (prev) {
                    var nt = __spreadArray([], prev, true);
                    var td = __assign({}, nt[index].data);
                    var ncd = __spreadArray([], td.cellData, true);
                    selectedCells.forEach(function (ck) {
                        var _a, _b;
                        var _c = ck.split('_').map(Number), r = _c[0], c = _c[1];
                        var ci = r * numCols + c;
                        if (!((_b = (_a = ncd[ci].data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.editing))
                            ncd[ci].data = __assign(__assign({}, ncd[ci].data), { backgroundColor: p.color });
                    });
                    td.cellData = ncd;
                    nt[index] = __assign(__assign({}, nt[index]), { data: td });
                    if (!saveStateGuardRef.current) {
                        saveStateGuardRef.current = true;
                        saveState({
                            type: 'table',
                            id: index,
                            propertyName: 'colorChange',
                            old: oldCC_1,
                            new: JSON.stringify(nt[index]),
                        });
                    }
                    return nt;
                });
                return;
            }
            // Non-cell-select mode: change text color
            var activeCellTool = selectedToolRef.current.find(function (t) { return typeof t === 'string' && t.startsWith("tableText_".concat(index, "_")); });
            // If a cell is being edited AND text is selected, changeColour in MathEditor
            // handles scenario 4 — don't also update cell.data.color or it would recolor
            // non-selected text via the MathEditor useEffect.
            if (activeCellTool) {
                var sel = window.getSelection();
                if (sel && !sel.isCollapsed)
                    return;
            }
            var oldCC = JSON.stringify(tabledataRef.current);
            saveStateGuardRef.current = false;
            setTable(function (prev) {
                var nt = __spreadArray([], prev, true);
                var td = __assign({}, nt[index].data);
                if (activeCellTool) {
                    // Active cell: update only that cell's text color
                    var parts = activeCellTool.split('_');
                    var r = Number(parts[2]);
                    var c = Number(parts[3]);
                    var ci = r * numCols + c;
                    var ncd = __spreadArray([], td.cellData, true);
                    ncd[ci] = __assign(__assign({}, ncd[ci]), { data: __assign(__assign({}, ncd[ci].data), { color: p.color, text: stripInlineColor(ncd[ci].data.text) }) });
                    td.cellData = ncd;
                }
                else {
                    // Whole table selected: update all cells' text color
                    td.cellData = td.cellData.map(function (cell) { return (__assign(__assign({}, cell), { data: __assign(__assign({}, cell.data), { color: p.color, text: stripInlineColor(cell.data.text) }) })); });
                }
                nt[index] = __assign(__assign({}, nt[index]), { data: td });
                if (!saveStateGuardRef.current) {
                    saveStateGuardRef.current = true;
                    saveState({
                        type: 'table',
                        id: index,
                        propertyName: 'textColorChange',
                        old: oldCC,
                        new: JSON.stringify(nt[index]),
                    });
                }
                return nt;
            });
        };
        var handleCLT = function (d) {
            if (d.tableIndex !== index)
                return;
            // Compute new lock state for immediate toolbar notification (setTable logic below unchanged).
            var ctlD = [];
            if (isCellSelectMode && selectedCells.size > 0)
                selectedCells.forEach(function (ck) { return ctlD.push(ck); });
            else {
                var te = selectedTool.find(function (id) { return id.startsWith("tableText_".concat(index, "_")); });
                if (te) {
                    var _a = te.split('_'), r = _a[2], c = _a[3];
                    ctlD.push("".concat(r, "_").concat(c));
                }
            }
            // Table-level selection: lock/unlock all cells
            if (ctlD.length === 0) {
                for (var r = 0; r < numRows; r++)
                    for (var c = 0; c < numCols; c++)
                        ctlD.push("".concat(r, "_").concat(c));
            }
            var allLockedD = ctlD.length > 0 &&
                ctlD.every(function (ck) {
                    var _a, _b, _c;
                    var _d = ck.split('_').map(Number), r = _d[0], c = _d[1];
                    return ((_c = (_b = (_a = tabledataRef.current.data.cellData[r * numCols + c]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing) === true;
                });
            var ensD = !allLockedD;
            customEventHandler.dispatchEvent('tableCellLockState', { locked: ensD });
            var oldCL = JSON.stringify(tabledataRef.current);
            saveStateGuardRef.current = false;
            setTable(function (prev) {
                var nt = __spreadArray([], prev, true);
                var td = __assign({}, nt[index].data);
                var ncd = __spreadArray([], td.cellData, true), ctl = [];
                if (isCellSelectMode && selectedCells.size > 0)
                    selectedCells.forEach(function (ck) { return ctl.push(ck); });
                else {
                    var te = selectedTool.find(function (id) { return id.startsWith("tableText_".concat(index, "_")); });
                    if (te) {
                        var _a = te.split('_'), r = _a[2], c = _a[3];
                        ctl.push("".concat(r, "_").concat(c));
                    }
                }
                if (ctl.length === 0) {
                    var cols = td.colWidth.length;
                    var rows = td.rowsHeight.length;
                    for (var r = 0; r < rows; r++)
                        for (var c = 0; c < cols; c++)
                            ctl.push("".concat(r, "_").concat(c));
                }
                var allLocked = ctl.length > 0 &&
                    ctl.every(function (ck) {
                        var _a, _b, _c;
                        var _d = ck.split('_').map(Number), r = _d[0], c = _d[1];
                        return ((_c = (_b = (_a = ncd[r * numCols + c]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing) === true;
                    });
                var ens = !allLocked;
                ctl.forEach(function (ck) {
                    var _a = ck.split('_').map(Number), r = _a[0], c = _a[1];
                    var cell = __assign({}, ncd[r * numCols + c]);
                    cell.data = __assign(__assign({}, cell.data), { lock: __assign(__assign({}, (cell.data.lock || { positioning: false })), { editing: ens }) });
                    ncd[r * numCols + c] = cell;
                });
                td.cellData = ncd;
                td.lock = __assign(__assign({}, (td.lock || { positioning: false })), { editing: ens });
                nt[index] = __assign(__assign({}, nt[index]), { data: td });
                if (!saveStateGuardRef.current) {
                    saveStateGuardRef.current = true;
                    saveState({
                        type: 'table',
                        id: index,
                        propertyName: 'cellLock',
                        old: oldCL,
                        new: JSON.stringify(nt[index]),
                    });
                }
                return nt;
            });
        };
        customEventHandler.addEventListener('bottomPanel', handleBP);
        customEventHandler.addEventListener('colourChange', handleCC);
        customEventHandler.addEventListener('tableContentLockToggle', handleCLT);
        customEventHandler.addEventListener('cellDuplicate', handleCellDuplicate);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', handleBP);
            customEventHandler.removeEventListener('colourChange', handleCC);
            customEventHandler.removeEventListener('tableContentLockToggle', handleCLT);
            customEventHandler.removeEventListener('cellDuplicate', handleCellDuplicate);
        };
    }, [isFocused, index, isCellSelectMode, selectedCells, selectedTool, numCols, numRows, setTable]);
    // When a specific cell is active (e.g. via "Toolbar" context menu), keep only that
    // cell's editorDiv in the tab order so Shift+Tab from the bottom panel lands on the
    // correct cell. When no cell is active, restore natural tabbability.
    useEffect(function () {
        var activeCellTool = selectedTool.find(function (t) { return t.startsWith("tableText_".concat(index, "_")) && t.split('_').length >= 4; });
        for (var r = 0; r < numRows; r++) {
            for (var c = 0; c < numCols; c++) {
                var el = document.getElementById("editor_tableText_".concat(index, "_").concat(r, "_").concat(c));
                if (!el)
                    continue;
                if (activeCellTool) {
                    el.tabIndex = activeCellTool === "tableText_".concat(index, "_").concat(r, "_").concat(c) ? 0 : -1;
                }
                else if (isFocused) {
                    // Picker buttons cover cells in picker mode — keep editorDivs out of tab order
                    // so Shift+Tab from outside the table lands on the sentinel, not a cell editor.
                    el.tabIndex = -1;
                }
                else {
                    el.removeAttribute('tabindex');
                }
            }
        }
    }, [selectedTool, isFocused, index, numRows, numCols]);
    // Focus first cell picker button when dispatched from context menu "Navigate Cells"
    useEffect(function () {
        var handler = function (data) {
            if (data.manipulativeId !== "tableText_".concat(index))
                return;
            setTimeout(function () { var _a; return (_a = pickerFirstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        };
        customEventHandler.addEventListener('openTableCellPicker', handler);
        return function () { return customEventHandler.removeEventListener('openTableCellPicker', handler); };
    }, [index]);
    /** ---------------- SUB-COMPONENTS ---------------- */
    var tableAddImgSrc = btnData
        ? __assign(__assign({}, btnData.add.imgSrc), { hover: './assets/images/icons/Larger_H.svg', hoverdark: './assets/images/icons/Larger_H.svg' }) : null;
    var tableDeleteImgSrc = btnData
        ? __assign(__assign({}, btnData.delete.imgSrc), { hover: './assets/images/icons/ScaleDown_H.svg', hoverdark: './assets/images/icons/ScaleDown_H.svg' }) : null;
    var controlsVisible = isFocused && !isPositionLocked && !isCellSelectMode;
    var TableController = function (_a) {
        var count = _a.count, positions = _a.positions, type = _a.type, tableIdx = _a.index, controller = _a.controller;
        return (_jsx("div", { className: "".concat(type, "ControlsContainer"), "aria-hidden": "true", children: btnData &&
                tableAddImgSrc &&
                tableDeleteImgSrc &&
                Array(count)
                    .fill(0)
                    .map(function (_, i) { return (_jsxs("div", { className: "".concat(type, "Control"), style: __assign({}, positions[i]), children: [_jsx(ButtonComponent, { imgSrc: tableAddImgSrc, defaultCSS: "btn", disabled: !controlsVisible || count >= 20, clickHandler: function () { return controller(tableIdx, i, 'add', type); }, showToolTip: controlsVisible, isParentRelative: true, direction: type === 'column' ? 'top' : 'right', title: type === 'column' ? 'text.tableAddColumn' : 'text.tableAddRow', tabIndex: -1 }), _jsx(ButtonComponent, { imgSrc: tableDeleteImgSrc, defaultCSS: "btn", disabled: !controlsVisible || count <= 1, clickHandler: function () { return controller(tableIdx, i, 'delete', type); }, showToolTip: controlsVisible, isParentRelative: true, direction: type === 'column' ? 'top' : 'right', title: type === 'column' ? 'text.tableDeleteColumn' : 'text.tableDeleteRow', tabIndex: -1 })] }, "".concat(type, "-").concat(i))); }) }));
    };
    return (_jsxs("div", { ref: containerRef, className: "tableManipContainer", "data-table-index": index, "data-manipulative-id": "tableText_".concat(index), onTouchStart: isFocused ? onMouseDown : undefined, style: {
            position: 'absolute',
            left: (localTablePos === null || localTablePos === void 0 ? void 0 : localTablePos.x) || 0,
            top: (localTablePos === null || localTablePos === void 0 ? void 0 : localTablePos.y) || 0,
            overflow: 'visible',
            padding: '0px',
            cursor: isCellSelectMode
                ? 'cell'
                : isPositionLocked
                    ? 'default'
                    : dragStated.current
                        ? 'grabbing'
                        : isFocused
                            ? 'grab'
                            : 'pointer',
            boxShadow: isFocused ? '0px 0px 20px #5063AE' : undefined,
            // CSS-only bring-to-front while actively dragging (permanent stacking
            // is fixed-category, not array-order, for tables — this only lifts a
            // table above other overlapping tables). Isolation contains this
            // table's own internal z-indices (move handle, resize handles,
            // column/row controls, cell picker button) so they can't leak out and
            // compete with other manipulatives' own boosts.
            zIndex: isDragging ? 9999 : undefined,
            isolation: 'isolate',
        }, children: [_jsx("div", { style: {
                    opacity: controlsVisible ? 1 : 0,
                    pointerEvents: controlsVisible ? undefined : 'none',
                    cursor: 'auto',
                }, children: _jsx(TableController, { count: numCols, positions: colPos, type: "column", index: index, controller: controller }) }), _jsx("div", { style: {
                    opacity: controlsVisible ? 1 : 0,
                    pointerEvents: controlsVisible ? undefined : 'none',
                    cursor: 'auto',
                }, children: _jsx(TableController, { count: numRows, positions: rowPos, type: "row", index: index, controller: controller }) }), !isPositionLocked && !panningEnable && (_jsx(ButtonComponent, { defaultCSS: "tableMoveHandle", normalCSS: "tableMoveHandleBtn".concat(dragStated.current ? ' tableMoveHandleBtn--grabbing' : ''), ariaLabel: (_b = t('text.moveFrame')) !== null && _b !== void 0 ? _b : 'Move table', tabIndex: -1, ariaHidden: true, type: "imageType1", noAudio: true, imgSrc: (_c = btnConfig.moveNumberFrame) === null || _c === void 0 ? void 0 : _c.imgSrc, onMouseDown: function (e) {
                    e.stopPropagation();
                    onMouseDown(e);
                }, onTouchStart: function (e) {
                    e.stopPropagation();
                    onMouseDown(e);
                } })), _jsxs("div", { ref: tableWrapperRef, style: { position: 'relative', display: 'inline-block', verticalAlign: 'top' }, children: [_jsx("button", { ref: sentinelBtnRef, "data-manipulative-id": "tableText_".concat(index), "aria-label": t('text.tableGridLabel', { rows: numRows, cols: numCols }), tabIndex: 0, className: "tableManipSentinel", onKeyDown: handleContainerKeyDown, onFocus: function () {
                            if (suppressFocusRedirectRef.current) {
                                suppressFocusRedirectRef.current = false;
                            }
                        }, onClick: handleContainerClick, onMouseDown: onMouseDown, onTouchStart: onMouseDown, style: {
                            position: 'absolute',
                            inset: 0,
                            padding: 0,
                            border: 0,
                            background: 'transparent',
                            // Not focused: sit on top (zIndex) to catch the initial click/tap.
                            // Focused: sink behind (no zIndex) with pointerEvents:none so clicks
                            // reach cells and picker buttons below.
                            // zIndex: isFocused ? undefined : 10,
                            pointerEvents: isFocused ? 'none' : 'auto',
                            cursor: 'inherit',
                        } }), _jsx("button", { tabIndex: -1, "aria-hidden": true, className: "resize-handle resize-handle-nw", style: { visibility: isFocused && !isPositionLocked ? 'visible' : 'hidden' }, onMouseDown: function (e) { return onResizeStart(e, 'nw'); }, onTouchStart: function (e) { return onResizeStart(e, 'nw'); } }), _jsx("button", { tabIndex: -1, "aria-hidden": true, className: "resize-handle resize-handle-ne", style: { visibility: isFocused && !isPositionLocked ? 'visible' : 'hidden' }, onMouseDown: function (e) { return onResizeStart(e, 'ne'); }, onTouchStart: function (e) { return onResizeStart(e, 'ne'); } }), _jsx("button", { tabIndex: -1, "aria-hidden": true, className: "resize-handle resize-handle-sw", style: { visibility: isFocused && !isPositionLocked ? 'visible' : 'hidden' }, onMouseDown: function (e) { return onResizeStart(e, 'sw'); }, onTouchStart: function (e) { return onResizeStart(e, 'sw'); } }), _jsx("button", { tabIndex: -1, "aria-hidden": true, className: "resize-handle resize-handle-se", style: { visibility: isFocused && !isPositionLocked ? 'visible' : 'hidden' }, onMouseDown: function (e) { return onResizeStart(e, 'se'); }, onTouchStart: function (e) { return onResizeStart(e, 'se'); } }), _jsx("table", { className: "tableManipTable", role: "presentation", ref: tableRef, "aria-hidden": !isFocused || undefined, style: { userSelect: 'none', width: colWidth.reduce(function (a, b) { return a + b; }, 0) }, children: _jsx("tbody", { children: tableRows.map(function (row, rowIndex) { return (_jsx("tr", { children: row.map(function (cell, colIndex) {
                                    var _a, _b, _c;
                                    var cellKey = "".concat(rowIndex, "_").concat(colIndex);
                                    var isCellSelected = selectedCells.has(cellKey);
                                    var isEditingAnyCell = selectedTool.some(function (t) { return t.startsWith("tableText_".concat(index, "_")) && t.split('_').length >= 4; });
                                    // Picker mode: table active but no specific cell being edited, OR in cell-select mode
                                    var cellPickerMode = isFocused && (!isEditingAnyCell || isCellSelectMode);
                                    var activeCellTool = selectedTool.find(function (t) { return t.startsWith("tableText_".concat(index, "_")) && t.split('_').length >= 4; });
                                    var isThisCellActive = !!activeCellTool &&
                                        activeCellTool === "tableText_".concat(index, "_").concat(rowIndex, "_").concat(colIndex);
                                    var isFirstCell = rowIndex === 0 && colIndex === 0;
                                    return (_jsxs("td", { className: "tableManipTd ".concat(isCellSelected ? 'cellSelected' : ''), style: (function () {
                                            var _a, _b, _c, _d, _e, _f, _g;
                                            var tableBW = borderWidth || 2;
                                            var myBW = ((_b = (_a = cellData[rowIndex * numCols + colIndex]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.borderWidth) || tableBW;
                                            return {
                                                height: Math.max(60, rowsHeight[rowIndex] || 60),
                                                width: Math.max(60, colWidth[colIndex] || 60),
                                                backgroundColor: ((_c = cell === null || cell === void 0 ? void 0 : cell.data) === null || _c === void 0 ? void 0 : _c.backgroundColor)
                                                    ? "color-mix(in srgb, ".concat(cell.data.backgroundColor, " 50%, #ffffff)")
                                                    : '#ffffff',
                                                border: "".concat(myBW, "px solid #5063ae"),
                                                fontWeight: ((_d = cell === null || cell === void 0 ? void 0 : cell.data) === null || _d === void 0 ? void 0 : _d.bold) ? 'bold' : undefined,
                                                fontStyle: ((_e = cell === null || cell === void 0 ? void 0 : cell.data) === null || _e === void 0 ? void 0 : _e.italic) ? 'italic' : undefined,
                                                textDecoration: ((_f = cell === null || cell === void 0 ? void 0 : cell.data) === null || _f === void 0 ? void 0 : _f.underline) ? 'underline' : undefined,
                                                textAlign: ((_g = cell === null || cell === void 0 ? void 0 : cell.data) === null || _g === void 0 ? void 0 : _g.alignment) || undefined,
                                            };
                                        })(), onClick: function (e) {
                                            if (isCellSelectMode) {
                                                e.stopPropagation();
                                                setSelectedCells(function (prev) {
                                                    var newSet = new Set(prev);
                                                    if (newSet.has(cellKey))
                                                        newSet.delete(cellKey);
                                                    else
                                                        newSet.add(cellKey);
                                                    return newSet;
                                                });
                                            }
                                        }, onContextMenu: function (ev) {
                                            var _a;
                                            if (isCellSelectMode)
                                                return;
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                            var btn = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-picker-cell=\"".concat(rowIndex, "_").concat(colIndex, "\"]"));
                                            btn === null || btn === void 0 ? void 0 : btn.focus();
                                            handlePickerCellActivate(rowIndex, colIndex, ev.currentTarget, {
                                                x: ev.clientX,
                                                y: ev.clientY,
                                            });
                                        }, children: [_jsx("button", { ref: isFirstCell ? pickerFirstBtnRef : undefined, className: "tableCellPickerBtn", tabIndex: cellPickerMode ? 0 : -1, "aria-hidden": !cellPickerMode && isThisCellActive ? true : undefined, style: cellPickerMode ? undefined : { pointerEvents: 'none' }, "aria-pressed": isCellSelectMode ? isCellSelected : undefined, "aria-label": isCellSelectMode
                                                    ? isCellSelected
                                                        ? t('text.tableCellSelectedLabel', {
                                                            row: rowIndex + 1,
                                                            col: colIndex + 1,
                                                        })
                                                        : t('text.tableCellLabel', { row: rowIndex + 1, col: colIndex + 1 })
                                                    : isThisCellActive
                                                        ? t('text.tableCellActiveLabel', {
                                                            row: rowIndex + 1,
                                                            col: colIndex + 1,
                                                        })
                                                        : t('text.tableCellLabel', { row: rowIndex + 1, col: colIndex + 1 }), "data-picker-cell": "".concat(rowIndex, "_").concat(colIndex), onMouseDown: function (ev) {
                                                    // Prevent browser from auto-focusing on right-click;
                                                    // onContextMenu will focus the button manually.
                                                    if (ev.button === 2)
                                                        ev.preventDefault();
                                                }, onKeyDown: function (ev) {
                                                    var _a, _b, _c, _d, _e, _f, _g;
                                                    ev.stopPropagation();
                                                    if (ev.key === 'Escape') {
                                                        ev.preventDefault();
                                                        (_a = sentinelBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                                        return;
                                                    }
                                                    if (ev.key === 'Enter' || ev.key === ' ') {
                                                        ev.preventDefault();
                                                        if (isCellSelectMode) {
                                                            var ck_1 = "".concat(rowIndex, "_").concat(colIndex);
                                                            var willSelect = !selectedCells.has(ck_1);
                                                            setSelectedCells(function (prev) {
                                                                var next = new Set(prev);
                                                                if (next.has(ck_1))
                                                                    next.delete(ck_1);
                                                                else
                                                                    next.add(ck_1);
                                                                return next;
                                                            });
                                                            announce(willSelect
                                                                ? t('text.tableCellSelected', {
                                                                    row: rowIndex + 1,
                                                                    col: colIndex + 1,
                                                                })
                                                                : t('text.tableCellDeselected', {
                                                                    row: rowIndex + 1,
                                                                    col: colIndex + 1,
                                                                }));
                                                        }
                                                        else {
                                                            handlePickerCellActivate(rowIndex, colIndex, ev.currentTarget);
                                                        }
                                                        return;
                                                    }
                                                    if (ev.key === 'ContextMenu' || (ev.shiftKey && ev.key === 'F10')) {
                                                        if (isCellSelectMode)
                                                            return;
                                                        ev.preventDefault();
                                                        handlePickerCellActivate(rowIndex, colIndex, ev.currentTarget);
                                                        return;
                                                    }
                                                    // Arrow key navigation
                                                    var nr = rowIndex, nc = colIndex;
                                                    if (ev.key === 'ArrowRight') {
                                                        ev.preventDefault();
                                                        nc = Math.min(colIndex + 1, numCols - 1);
                                                    }
                                                    else if (ev.key === 'ArrowLeft') {
                                                        ev.preventDefault();
                                                        nc = Math.max(colIndex - 1, 0);
                                                    }
                                                    else if (ev.key === 'ArrowDown') {
                                                        ev.preventDefault();
                                                        nr = Math.min(rowIndex + 1, numRows - 1);
                                                    }
                                                    else if (ev.key === 'ArrowUp') {
                                                        ev.preventDefault();
                                                        nr = Math.max(rowIndex - 1, 0);
                                                    }
                                                    if (nr !== rowIndex || nc !== colIndex) {
                                                        var btn = (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.querySelector("[data-picker-cell=\"".concat(nr, "_").concat(nc, "\"]"));
                                                        btn === null || btn === void 0 ? void 0 : btn.focus();
                                                        if (isCellSelectMode) {
                                                            var isSelected = selectedCells.has("".concat(nr, "_").concat(nc));
                                                            announce(isSelected
                                                                ? t('text.tableCellSelected', { row: nr + 1, col: nc + 1 })
                                                                : t('text.tableCellLabel', { row: nr + 1, col: nc + 1 }));
                                                        }
                                                        return;
                                                    }
                                                    // Tab: skip contenteditables, jump directly to next/prev picker button
                                                    if (ev.key === 'Tab') {
                                                        ev.preventDefault();
                                                        var isLast = rowIndex === numRows - 1 && colIndex === numCols - 1;
                                                        var isFirst = rowIndex === 0 && colIndex === 0;
                                                        if (!ev.shiftKey) {
                                                            if (isLast) {
                                                                // Exit table forward — find the sentinel in the full page list,
                                                                // then skip to the first element after it that's outside the table.
                                                                var FOCUSABLE = 'a[href], button:not([disabled]):not([aria-disabled="true"]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([aria-disabled="true"]), [contenteditable="true"]';
                                                                var allOnPage = Array.from(document.querySelectorAll(FOCUSABLE)).filter(function (el) { return document.body.contains(el); });
                                                                var sentinelIdx = allOnPage.indexOf(sentinelBtnRef.current);
                                                                var next = allOnPage
                                                                    .slice(sentinelIdx + 1)
                                                                    .find(function (el) { var _a; return !((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(el)); });
                                                                next === null || next === void 0 ? void 0 : next.focus();
                                                            }
                                                            else {
                                                                var nc2 = colIndex + 1 < numCols ? colIndex + 1 : 0;
                                                                var nr2 = colIndex + 1 < numCols ? rowIndex : rowIndex + 1;
                                                                (_d = (_c = containerRef.current) === null || _c === void 0 ? void 0 : _c.querySelector("[data-picker-cell=\"".concat(nr2, "_").concat(nc2, "\"]"))) === null || _d === void 0 ? void 0 : _d.focus();
                                                            }
                                                        }
                                                        else {
                                                            if (isFirst) {
                                                                (_e = sentinelBtnRef.current) === null || _e === void 0 ? void 0 : _e.focus();
                                                            }
                                                            else {
                                                                var nc2 = colIndex - 1 >= 0 ? colIndex - 1 : numCols - 1;
                                                                var nr2 = colIndex - 1 >= 0 ? rowIndex : rowIndex - 1;
                                                                (_g = (_f = containerRef.current) === null || _f === void 0 ? void 0 : _f.querySelector("[data-picker-cell=\"".concat(nr2, "_").concat(nc2, "\"]"))) === null || _g === void 0 ? void 0 : _g.focus();
                                                            }
                                                        }
                                                    }
                                                }, onClick: function (ev) {
                                                    var _a, _b, _c, _d;
                                                    ev.stopPropagation();
                                                    if (isCellSelectMode) {
                                                        var ck_2 = "".concat(rowIndex, "_").concat(colIndex);
                                                        var willSelect = !selectedCells.has(ck_2);
                                                        setSelectedCells(function (prev) {
                                                            var next = new Set(prev);
                                                            if (next.has(ck_2))
                                                                next.delete(ck_2);
                                                            else
                                                                next.add(ck_2);
                                                            return next;
                                                        });
                                                        announce(willSelect
                                                            ? t('text.tableCellSelected', {
                                                                row: rowIndex + 1,
                                                                col: colIndex + 1,
                                                            })
                                                            : t('text.tableCellDeselected', {
                                                                row: rowIndex + 1,
                                                                col: colIndex + 1,
                                                            }));
                                                        return;
                                                    }
                                                    var cellKey = "".concat(index, "_").concat(rowIndex, "_").concat(colIndex);
                                                    var isLockedCell = ((_c = (_b = (_a = cellData[rowIndex * numCols + colIndex]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing) === true;
                                                    customEventHandler.dispatchEvent('tableCellLockState', {
                                                        locked: isLockedCell,
                                                    });
                                                    // On touch devices, VO/TalkBack synthetic clicks (detail===0)
                                                    // open the context menu. Real finger taps (detail===1) enter
                                                    // edit mode just like desktop — they are not AT interactions.
                                                    if (HAS_TOUCH && ev.detail === 0) {
                                                        handlePickerCellActivate(rowIndex, colIndex, ev.currentTarget);
                                                        return;
                                                    }
                                                    if (isLockedCell) {
                                                        pendingFocusRef.current = null;
                                                        setSelectedTool(["tableText_".concat(cellKey)]);
                                                    }
                                                    else {
                                                        ignoreNextTabShiftRef.current = true;
                                                        pendingFocusRef.current = cellKey;
                                                        // On touch (iPad): focus the editor directly from this onClick
                                                        // handler so iOS opens the software keyboard (iOS only shows the
                                                        // keyboard when focus() is called inside a user-gesture handler,
                                                        // not from a useEffect or rAF).
                                                        if (HAS_TOUCH) {
                                                            var editorEl = (_d = ev.currentTarget
                                                                .closest('td')) === null || _d === void 0 ? void 0 : _d.querySelector('.editorDiv');
                                                            editorEl === null || editorEl === void 0 ? void 0 : editorEl.focus({ preventScroll: true });
                                                        }
                                                        setTimeout(function () { return setSelectedTool(["tableText_".concat(cellKey)]); }, 0);
                                                    }
                                                }, onContextMenu: function (ev) {
                                                    ev.preventDefault();
                                                    ev.stopPropagation();
                                                    ev.currentTarget.focus();
                                                    handlePickerCellActivate(rowIndex, colIndex, ev.currentTarget, {
                                                        x: ev.clientX,
                                                        y: ev.clientY,
                                                    });
                                                } }), _jsx(MathEditor, { data: cell, color: ((_a = cell === null || cell === void 0 ? void 0 : cell.data) === null || _a === void 0 ? void 0 : _a.color) || ColorEnum[color], index: "".concat(index, "_").concat(rowIndex, "_").concat(colIndex), cb: onUpdateText, idPreffix: "tableText", dataPlaceholder: "\u200B", placeHolder: "", isTextUpdate: false, keepSelectedOnInternalBlur: true, scrollIntoViewOnFocus: true, outerAriaHidden: cellPickerMode || (isEditingAnyCell && !isThisCellActive), suppressOuterGroup: true, editorAriaLabel: t('text.tableCellLabel', {
                                                    row: rowIndex + 1,
                                                    col: colIndex + 1,
                                                }), viewonly: ((_c = (_b = cell === null || cell === void 0 ? void 0 : cell.data) === null || _b === void 0 ? void 0 : _b.lock) === null || _c === void 0 ? void 0 : _c.editing) === true || isCellSelectMode, onTouchFocusSelf: function () { ignoreNextTabShiftRef.current = true; }, keypadButtons: bottomToolConfig === null || bottomToolConfig === void 0 ? void 0 : bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig === null || bottomToolConfig === void 0 ? void 0 : bottomToolConfig.mathKeypadBottom, onKeyDown: function (e) {
                                                    var _a, _b, _c, _d, _e;
                                                    if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        // MathEditor's own Escape handler fires synchronously after this
                                                        // callback and calls setSelectedTool([]), overwriting any call we
                                                        // make here. Defer to a setTimeout so our re-select wins.
                                                        setTimeout(function () {
                                                            suppressFocusRedirectRef.current = true;
                                                            setSelectedTool(["tableText_".concat(index)]);
                                                            setTimeout(function () {
                                                                var _a, _b;
                                                                (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-picker-cell=\"".concat(rowIndex, "_").concat(colIndex, "\"]"))) === null || _b === void 0 ? void 0 : _b.focus();
                                                            }, 0);
                                                        }, 0);
                                                        return;
                                                    }
                                                    if (e.key !== 'Tab')
                                                        return;
                                                    e.preventDefault();
                                                    // iPadOS bug: first Tab after a tap-entered cell fires with shiftKey=true.
                                                    // Consume the flag and force forward navigation for that one press.
                                                    var effectiveShiftKey = HAS_TOUCH && ignoreNextTabShiftRef.current ? false : e.shiftKey;
                                                    ignoreNextTabShiftRef.current = false;
                                                    var isLastCell = rowIndex === numRows - 1 && colIndex === numCols - 1;
                                                    var isFirstCell2 = rowIndex === 0 && colIndex === 0;
                                                    if (!effectiveShiftKey) {
                                                        if (isLastCell) {
                                                            setSelectedTool(["tableText_".concat(index)]);
                                                            var FOCUSABLE = 'a[href], button:not([disabled]):not([aria-disabled="true"]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([aria-disabled="true"]), [contenteditable="true"]';
                                                            var allOnPage = Array.from(document.querySelectorAll(FOCUSABLE)).filter(function (el) { return document.body.contains(el); });
                                                            var sentinelIdx = allOnPage.indexOf(sentinelBtnRef.current);
                                                            var next = allOnPage
                                                                .slice(sentinelIdx + 1)
                                                                .find(function (el) { var _a; return !((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(el)); });
                                                            next === null || next === void 0 ? void 0 : next.focus();
                                                        }
                                                        else {
                                                            var nc = colIndex + 1 < numCols ? colIndex + 1 : 0;
                                                            var nr = colIndex + 1 < numCols ? rowIndex : rowIndex + 1;
                                                            if (HAS_TOUCH) {
                                                                touchFocusRef.current = false;
                                                                pendingFocusRef.current = "".concat(index, "_").concat(nr, "_").concat(nc);
                                                                setSelectedTool(["tableText_".concat(index, "_").concat(nr, "_").concat(nc)]);
                                                                // Microtask runs before repaint and typically before iOS native
                                                                // Tab traversal, eliminating the backward-flash on first Tab.
                                                                // pendingFocusRef remains set as a useEffect safety net in case
                                                                // iOS's OS-level navigation fires after the microtask.
                                                                var _tk_1 = "".concat(index, "_").concat(nr, "_").concat(nc);
                                                                Promise.resolve().then(function () {
                                                                    var _a;
                                                                    (_a = document
                                                                        .getElementById("editor_tableText_".concat(_tk_1))) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
                                                                });
                                                            }
                                                            else {
                                                                setSelectedTool(["tableText_".concat(index)]);
                                                                (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-picker-cell=\"".concat(nr, "_").concat(nc, "\"]"))) === null || _b === void 0 ? void 0 : _b.focus();
                                                            }
                                                        }
                                                    }
                                                    else {
                                                        if (isFirstCell2) {
                                                            setSelectedTool(["tableText_".concat(index)]);
                                                            (_c = sentinelBtnRef.current) === null || _c === void 0 ? void 0 : _c.focus();
                                                        }
                                                        else {
                                                            var nc = colIndex - 1 >= 0 ? colIndex - 1 : numCols - 1;
                                                            var nr = colIndex - 1 >= 0 ? rowIndex : rowIndex - 1;
                                                            if (HAS_TOUCH) {
                                                                touchFocusRef.current = false;
                                                                pendingFocusRef.current = "".concat(index, "_").concat(nr, "_").concat(nc);
                                                                setSelectedTool(["tableText_".concat(index, "_").concat(nr, "_").concat(nc)]);
                                                                var _tk_2 = "".concat(index, "_").concat(nr, "_").concat(nc);
                                                                Promise.resolve().then(function () {
                                                                    var _a;
                                                                    (_a = document
                                                                        .getElementById("editor_tableText_".concat(_tk_2))) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
                                                                });
                                                            }
                                                            else {
                                                                setSelectedTool(["tableText_".concat(index)]);
                                                                (_e = (_d = containerRef.current) === null || _d === void 0 ? void 0 : _d.querySelector("[data-picker-cell=\"".concat(nr, "_").concat(nc, "\"]"))) === null || _e === void 0 ? void 0 : _e.focus();
                                                            }
                                                        }
                                                    }
                                                }, suppressAutoFocusWhenToolbarActive: true, onTab: function (shiftKey) {
                                                    var _a, _b, _c, _d, _e, _f;
                                                    var effectiveShiftKey = HAS_TOUCH && ignoreNextTabShiftRef.current ? false : shiftKey;
                                                    ignoreNextTabShiftRef.current = false;
                                                    var isLastCell = rowIndex === numRows - 1 && colIndex === numCols - 1;
                                                    var isFirstCell2 = rowIndex === 0 && colIndex === 0;
                                                    if (!effectiveShiftKey) {
                                                        if (isLastCell) {
                                                            setSelectedTool(["tableText_".concat(index)]);
                                                            var FOCUSABLE = 'a[href], button:not([disabled]):not([aria-disabled="true"]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([aria-disabled="true"]), [contenteditable="true"]';
                                                            var allOnPage = Array.from(document.querySelectorAll(FOCUSABLE)).filter(function (el) { return document.body.contains(el); });
                                                            var sentinelIdx = allOnPage.indexOf(sentinelBtnRef.current);
                                                            (_a = allOnPage
                                                                .slice(sentinelIdx + 1)
                                                                .find(function (el) { var _a; return !((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(el)); })) === null || _a === void 0 ? void 0 : _a.focus();
                                                        }
                                                        else {
                                                            var nc = colIndex + 1 < numCols ? colIndex + 1 : 0;
                                                            var nr = colIndex + 1 < numCols ? rowIndex : rowIndex + 1;
                                                            if (HAS_TOUCH) {
                                                                touchFocusRef.current = false;
                                                                pendingFocusRef.current = "".concat(index, "_").concat(nr, "_").concat(nc);
                                                                setSelectedTool(["tableText_".concat(index, "_").concat(nr, "_").concat(nc)]);
                                                                var _tk_3 = "".concat(index, "_").concat(nr, "_").concat(nc);
                                                                Promise.resolve().then(function () {
                                                                    var _a;
                                                                    (_a = document
                                                                        .getElementById("editor_tableText_".concat(_tk_3))) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
                                                                });
                                                            }
                                                            else {
                                                                setSelectedTool(["tableText_".concat(index)]);
                                                                (_c = (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.querySelector("[data-picker-cell=\"".concat(nr, "_").concat(nc, "\"]"))) === null || _c === void 0 ? void 0 : _c.focus();
                                                            }
                                                        }
                                                    }
                                                    else {
                                                        if (isFirstCell2) {
                                                            setSelectedTool(["tableText_".concat(index)]);
                                                            (_d = sentinelBtnRef.current) === null || _d === void 0 ? void 0 : _d.focus();
                                                        }
                                                        else {
                                                            var nc = colIndex - 1 >= 0 ? colIndex - 1 : numCols - 1;
                                                            var nr = colIndex - 1 >= 0 ? rowIndex : rowIndex - 1;
                                                            if (HAS_TOUCH) {
                                                                touchFocusRef.current = false;
                                                                pendingFocusRef.current = "".concat(index, "_").concat(nr, "_").concat(nc);
                                                                setSelectedTool(["tableText_".concat(index, "_").concat(nr, "_").concat(nc)]);
                                                                var _tk_4 = "".concat(index, "_").concat(nr, "_").concat(nc);
                                                                Promise.resolve().then(function () {
                                                                    var _a;
                                                                    (_a = document
                                                                        .getElementById("editor_tableText_".concat(_tk_4))) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
                                                                });
                                                            }
                                                            else {
                                                                setSelectedTool(["tableText_".concat(index)]);
                                                                (_f = (_e = containerRef.current) === null || _e === void 0 ? void 0 : _e.querySelector("[data-picker-cell=\"".concat(nr, "_").concat(nc, "\"]"))) === null || _f === void 0 ? void 0 : _f.focus();
                                                            }
                                                        }
                                                    }
                                                }, ref: function (el) {
                                                    var key = "".concat(index, "_").concat(rowIndex, "_").concat(colIndex);
                                                    if (el)
                                                        mathEditorRefs.current[key] = el;
                                                    else
                                                        delete mathEditorRefs.current[key];
                                                } }), isCellSelectMode && (_jsx("div", { className: "cellSelectOverlay", style: {
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    zIndex: 1002,
                                                    cursor: 'cell',
                                                    background: 'transparent',
                                                }, onMouseDown: function (e) {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }, onTouchEnd: function (e) {
                                                    e.stopPropagation();
                                                    setSelectedCells(function (prev) {
                                                        var newSet = new Set(prev);
                                                        if (newSet.has(cellKey))
                                                            newSet.delete(cellKey);
                                                        else
                                                            newSet.add(cellKey);
                                                        return newSet;
                                                    });
                                                }, onClick: function (e) {
                                                    e.stopPropagation();
                                                    setSelectedCells(function (prev) {
                                                        var newSet = new Set(prev);
                                                        if (newSet.has(cellKey))
                                                            newSet.delete(cellKey);
                                                        else
                                                            newSet.add(cellKey);
                                                        return newSet;
                                                    });
                                                } }))] }, (cell === null || cell === void 0 ? void 0 : cell.cellId) || "".concat(rowIndex, "_").concat(colIndex)));
                                }) }, rowIndex)); }) }) })] })] }));
};
export default TableManipulativeComp;
